import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import API_CONFIG from '../config/api';
import './SuperAdminDashboardPage.css';
// AdminTabs.css holds the shared styling every tab below depends on
// (.super-card, .spr-*, .lm-*, table/badge/modal classes, etc.). It used to
// load implicitly because this page imported the AdminTabs.js barrel, which
// imported the CSS; now that each tab is fetched from its own file (see the
// lazy() calls below) nothing else pulls this in, so it's imported directly
// and eagerly here instead — loading it up front, same as before, rather
// than only after whichever tab happens to be opened first.
import '../components/admin/AdminTabs.css';
import { DashboardBanner } from '../components/admin/DashboardBanner';
import wsService from '../utils/websocketService';
import { buildDashboardTrend } from '../utils/dashboardTrend';
// Every lazy tab component (plus its hover-preload counterpart, used by the
// sidebar in AdminDashboardLayout.js) lives in one shared registry so both
// files request the exact same import() for a given tab — see
// tabRegistry.js for why that sharing matters.
import {
    AnalyticsTab, PermissionsTab, RolesTab, SettingsTab, DatabaseTab, ActivityMonitorTab,
    ReportsTab, SupportTab, ApiManagementTab, PaymentGatewayTab, KycManagementTab, LoansTab,
    LeadsTab, SalesLeadsTab, DataRecordTab, BreDecisionTab, GlobalSearchTab, MyDashboardTab,
    InvestorDashboardTab, CollectionReportTab, StaffTargetsTab, BankAndTransactionsTab, WhatsAppMarketingTab
} from '../components/admin/tabRegistry';

// Load modal code only when the corresponding dialog is opened.
const CreateAdminModal = lazy(() => import('../components/admin/CreateAdminModal'));
const CreateUserModal = lazy(() => import('../components/admin/CreateUserModal'));
const EditAdminModal = lazy(() => import('../components/admin/EditAdminModal'));
const EditUserModal = lazy(() => import('../components/admin/EditUserModal'));

function SuperAdminDashboardPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [adminSession, setAdminSession] = useState(null);
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'dashboard');

    // Permission check helper
    const hasPermission = (permission) => {
        if (!adminSession) return false;
        if (adminSession.role === 'super_admin') return true;

        const userPerms = adminSession.permissions || [];
        if (Array.isArray(permission)) {
            return permission.some(p => userPerms.includes(p));
        }
        return userPerms.includes(permission);
    };

    // Tab visibility mapping
    const tabPermissions = {
        'overview': null, // Everyone can see overview
        'admins': 'admins.view_all',
        'users': ['users.view_all', 'users.create', 'users.edit', 'users.delete', 'users.verify_kyc', 'users.reset_password', 'users.agreement_upload'],
        'logs': 'logs.view',
        'globalSearch': 'search.view',
        'investorDashboard': 'investor.dashboard',
        'collectionReport': 'investor.dashboard',
        'analytics': 'analytics.view',
        'permissions': 'permissions.manage',
        'roles': 'roles.manage',
        'settings': 'settings.manage',
        'database': 'database.manage',
        'sanctionLetters': 'database.manage',
        'crifData': 'database.manage',
        'crifJson': 'database.manage',
        'crifHtml': 'database.manage',
        'aadhaarInfo': 'database.manage',
        'bankStatements': 'database.manage',
        'activity': 'activity.view',
        'reports': 'reports.view',
        'support': 'tickets.manage',
        'api': 'api.manage',
        'whatsappMarketing': ['api.manage', 'whatsapp.manage'],
        'payments': 'payments.manage',
        'kyc': 'kyc.manage',
        'loans': ['loans.view_all', 'loans.view', 'loans.create', 'loans.edit', 'loans.approve', 'loans.modify', 'loans.disburse', 'loans.delete', 'loans.cancel'],
        'queue-reloan': 'queue.reloan',
        'leads': ['leads.view', 'leads.create', 'leads.edit', 'leads.assign', 'leads.delete', 'leads.status'],
        'salesLeads': ['leads.view']
    };
    const [showCreateAdminModal, setShowCreateAdminModal] = useState(false);
    const [showCreateUserModal, setShowCreateUserModal] = useState(false);
    const [showEditAdminModal, setShowEditAdminModal] = useState(false);
    const [showEditUserModal, setShowEditUserModal] = useState(false);
    const fetchInFlightRef = useRef(false);
    const fetchQueuedRef = useRef(false);
    const refreshDataRef = useRef(() => {});
    const dataViewRef = useRef({});
    const dashboardAbortRef = useRef(null);

    // Sync activeTab with URL params
    useEffect(() => {
        const tab = searchParams.get('tab');
        if (tab && tab !== activeTab) {
            setActiveTab(tab);
        }
    }, [searchParams, activeTab]);

    // Role-based default landing tab when no tab is specified
    useEffect(() => {
        const tabParam = searchParams.get('tab');
        if (tabParam || !adminSession) return;
        const role = adminSession.role;
        if (['credit_executive', 'credit_head', 'sales_executive', 'sales_head', 'collection_executive', 'collection_head'].includes(role)) {
            setActiveTab('myDashboard');
        } else if (role === 'sales') {
            setActiveTab('leads');
        } else if (role === 'branch' || role === 'collection' || role === 'accounts') {
            setActiveTab('loans');
        }
    }, [adminSession, searchParams]);

    // Update URL when activeTab changes (for internal tab clicks)
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setSearchParams({ tab });
    };

    // Every tab below used to unmount the instant you left it
    // (`activeTab === 'x' && <X/>`), which meant its whole component — and
    // whatever it had already fetched — was thrown away, so switching back
    // ran its data fetch all over again with a loading spinner every single
    // time. Once a permitted tab has actually been opened this ref keeps
    // its key forever, and renderKeepAlive below leaves that tab mounted
    // (just hidden) rather than unmounting it, so returning to any tab
    // you've already visited this session is instant — no refetch, no
    // spinner. Mutating a ref during render is safe here: it only feeds
    // future renders (whether a *different* tab still keeps rendering this
    // one, hidden), never the current one, which already has the right
    // answer from the isActive check below.
    const visitedTabsRef = useRef(new Set());
    const renderKeepAlive = (tabKey, permitted, node) => {
        const isActive = activeTab === tabKey && permitted;
        if (isActive) visitedTabsRef.current.add(tabKey);
        if (!visitedTabsRef.current.has(tabKey)) return null;
        // Own Suspense boundary per tab, not one shared boundary around all
        // of them — otherwise opening a brand-new tab for the first time
        // would suspend the *whole* block and blank out every other tab
        // that's already loaded and just sitting there hidden.
        return (
            <div style={{ display: isActive ? 'block' : 'none' }}>
                <Suspense fallback={
                    <div className="tab-loading">
                        <div className="tab-loading-spinner" />
                        <span>Loading…</span>
                    </div>
                }>
                    {node}
                </Suspense>
            </div>
        );
    };

    // Helpers to format and derive overview KPIs
    const parseCurrencyToNumber = (value) => {
        if (typeof value === 'number') return value;
        if (!value) return 0;
        const numeric = String(value).replace(/[^\d.]/g, '');
        return Number(numeric) || 0;
    };

    const [stats, setStats] = useState({
        totalUsers: 0,
        totalAdmins: 0,
        activeLoans: 0,
        pendingApprovals: 0,
        pendingLoans: 0,
        totalLoans: 0,
        openTickets: 0,
        totalDisbursed: '₹0',
        systemHealth: 'Excellent',
        monthlyCollection: { dueCount: 0, dueAmount: 0, collectedCount: 0, collectedAmount: 0, percent: 0 },
        netProfit: { amount: 0, formatted: '₹0', totalDisbursed: 0, totalCollected: 0 },
        disbursal: { mtd: { gross: 0, net: 0 }, allTime: { gross: 0, net: 0 } },
        lifetime: {
            disbursedLoans: 0, disbursedGross: 0, disbursedNet: 0,
            collectedPrincipal: 0, collectedInterest: 0, collectedPenalty: 0,
            collectedProcessingFee: 0, collectedGst: 0, collected: 0
        }
    });
    const [leadSummary, setLeadSummary] = useState({
        total: 0,
        byStatus: {},
        myCount: 0,
        unassigned: 0
    });

    const [admins, setAdmins] = useState([]);
    const [adminSearch, setAdminSearch] = useState('');
    const [adminRoleFilter, setAdminRoleFilter] = useState('all');
    const [adminStatusFilter, setAdminStatusFilter] = useState('all');
    const [recentUsers, setRecentUsers] = useState([]);
    const [recentLoans, setRecentLoans] = useState([]);
    const [recentPayments, setRecentPayments] = useState([]);
    const [userSearch, setUserSearch] = useState('');
    const [usersPage, setUsersPage] = useState(1);
    const [usersTotalPages, setUsersTotalPages] = useState(1);
    const [systemLogs, setSystemLogs] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [logFilter, setLogFilter] = useState('');
    // eslint-disable-next-line no-unused-vars
    const [logSeverity, setLogSeverity] = useState('all');
    // eslint-disable-next-line no-unused-vars
    const [isLoading, setIsLoading] = useState(true);
    // eslint-disable-next-line no-unused-vars
    const [timeframe, setTimeframe] = useState('7d');
    const [dashboardError, setDashboardError] = useState('');
    const [dashboardUpdatedAt, setDashboardUpdatedAt] = useState(null);
    const [dashboardRefreshSignal, setDashboardRefreshSignal] = useState(0);
    const [selectedDisbursalPoint, setSelectedDisbursalPoint] = useState(null);
    const [selectedCollectionPoint, setSelectedCollectionPoint] = useState(null);

    // Custom Modal State
    const [modalConfig, setModalConfig] = useState({
        show: false,
        type: 'confirm', // 'confirm' or 'alert'
        title: '',
        message: '',
        onConfirm: null,
        confirmText: 'Confirm',
        cancelText: 'Cancel'
    });

    // Derived KPI helpers for richer dashboard storytelling
    const totalLoanCount = stats.totalLoans || 0;
    const approvalRate = totalLoanCount ? Math.round((stats.activeLoans / totalLoanCount) * 100) : 0;
    const pendingRate = totalLoanCount ? Math.round((stats.pendingLoans / totalLoanCount) * 100) : 0;
    const avgDisbursalPerUser = stats.totalUsers ? Math.round(parseCurrencyToNumber(stats.totalDisbursed) / Math.max(stats.totalUsers, 1)) : 0;
    const openTicketBacklog = stats.openTickets || stats.pendingApprovals || 0;
    const avgTicketSize = stats.activeLoans ? Math.round(parseCurrencyToNumber(stats.totalDisbursed) / Math.max(stats.activeLoans, 1)) : 0;
    const conversionRate = totalLoanCount ? Math.round((stats.activeLoans / totalLoanCount) * 100) : 0;
    const complianceScore = 100 - pendingRate;
    const totalDisbursedAmount = parseCurrencyToNumber(stats.totalDisbursed);

    const formatCurrency = (value) => `₹${Math.round(value || 0).toLocaleString('en-IN')}`;
    const formatDisplayDate = (value) => {
        if (!value) return '—';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '—';
        return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
    };
    const getStoredAdminSession = () => {
        const session = localStorage.getItem('adminSession');
        if (!session) return null;

        try {
            return JSON.parse(session);
        } catch (error) {
            console.error('Invalid admin session in storage:', error);
            localStorage.removeItem('adminSession');
            return null;
        }
    };

    const getDashboardSeries = () => ({
        label: timeframe === '30d' ? 'Last 30 days' : 'Last 7 days',
        series: Array.isArray(stats.trend)
            ? buildDashboardTrend(stats.trend, timeframe === '30d' ? 30 : 7, dashboardUpdatedAt || new Date())
            : []
    });

    // eslint-disable-next-line no-unused-vars
    const { label: dashboardWindowLabel, series: dashboardSeries } = getDashboardSeries();
    const visibleSeries = dashboardSeries.slice(-Math.min(5, dashboardSeries.length));
    // eslint-disable-next-line no-unused-vars
    const selectedDayData = selectedDisbursalPoint
        ? dashboardSeries.find((point) => point.id === selectedDisbursalPoint) || null
        : null;
    const visibleLoanTotal = visibleSeries.reduce((sum, point) => sum + point.loans, 0);
    const visibleAmountTotal = visibleSeries.reduce((sum, point) => sum + point.amount, 0);
    // eslint-disable-next-line no-unused-vars
    const activeVisibleDays = visibleSeries.filter((point) => point.amount > 0).length;
    // eslint-disable-next-line no-unused-vars
    const peakPoint = visibleSeries.reduce((best, point) => (
        !best || point.amount > best.amount ? point : best
    ), visibleSeries[0] || null);
    // eslint-disable-next-line no-unused-vars
    const avgLoansPerVisibleDay = visibleSeries.length ? (visibleLoanTotal / visibleSeries.length).toFixed(1) : '0.0';
    // eslint-disable-next-line no-unused-vars
    const avgAmountPerVisibleDay = visibleSeries.length ? Math.round(visibleAmountTotal / visibleSeries.length) : 0;
    const disbursalChartMax = Math.max(...dashboardSeries.map((point) => point.amount), 1);
    const chartPoints = dashboardSeries.map((point, index) => {
        const x = dashboardSeries.length === 1 ? 24 : (index / (dashboardSeries.length - 1)) * 100;
        const y = 100 - ((point.amount || 0) / disbursalChartMax) * 100;
        return {
            ...point,
            x,
            y: Number.isFinite(y) ? y : 100
        };
    });
    const chartPolyline = chartPoints.map((point) => `${point.x},${point.y}`).join(' ');
    // eslint-disable-next-line no-unused-vars
    const chartArea = chartPoints.length
        ? `0,100 ${chartPolyline} 100,100`
        : '0,100 100,100';
    // eslint-disable-next-line no-unused-vars
    const dashboardTimeline = dashboardSeries.slice(-10);
    const adminDisplayName = adminSession?.name || 'Admin';
    // eslint-disable-next-line no-unused-vars
    const adminDisplayRole = adminSession?.role
        ? adminSession.role.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
        : 'Super Admin';
    // eslint-disable-next-line no-unused-vars
    const adminAvatarInitials = adminDisplayName
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join('')
        .toUpperCase();
    // eslint-disable-next-line no-unused-vars
    const recentBorrowers = recentUsers.slice(0, 3);
    const portfolioBreakdownSource = [
        { label: 'Active Loans', value: Math.max(stats.activeLoans || 0, 1), color: '#5b4fd6' },
        { label: 'Pending Loans', value: Math.max(stats.pendingLoans || 0, 1), color: '#56c8f2' },
        { label: 'Recent Borrowers', value: Math.max(recentUsers.length || 0, 1), color: '#f3b347' }
    ];
    const portfolioTotal = portfolioBreakdownSource.reduce((sum, item) => sum + item.value, 0) || 1;
    const portfolioSegments = portfolioBreakdownSource.map((item) => ({
        ...item,
        percent: Math.round((item.value / portfolioTotal) * 100)
    }));
    // eslint-disable-next-line no-unused-vars
    const portfolioConic = portfolioSegments
        .map((item, index) => {
            const start = portfolioSegments
                .slice(0, index)
                .reduce((sum, segment) => sum + segment.percent, 0);
            const end = start + item.percent;
            return `${item.color} ${start}% ${end}%`;
        })
        .join(', ');
    const loanStatusBreakdown = recentLoans.reduce((acc, loan) => {
        const status = String(loan.status || 'unknown').toLowerCase();
        acc[status] = (acc[status] || 0) + 1;
        return acc;
    }, {});
    const loanStatusMax = Math.max(...Object.values(loanStatusBreakdown), 1);
    const STATUS_COLORS = {
        approved: '#16a34a', active: '#16a34a', disbursed: '#4f46e5', paid: '#16a34a',
        pending: '#f59e0b', in_progress: '#0ea5e9', rejected: '#ef4444', overdue: '#ef4444',
        closed: '#64748b', unknown: '#94a3b8'
    };
    const statusColor = (s) => STATUS_COLORS[String(s).toLowerCase()] || '#6366f1';
    const firstName = (adminDisplayName || 'Admin').split(' ')[0];
    // ── Portfolio figures from the server ───────────────────────────────────
    // These come from the same SQL fragments the Reports engine uses, so the
    // headline here and an exported Master Disbursed report agree.
    const disbursal = stats.disbursal || {};
    const disbursalMtd = disbursal.mtd || {};
    const lifetime = stats.lifetime || {};
    const queue = stats.queue || { total: 0, stages: [] };
    const portfolio = stats.portfolio || {};
    const risk = stats.risk || { buckets: [], parPercent: 0, overdueBalance: 0 };
    const collections = stats.collections || {};
    const revenue = stats.revenue || {};
    const funnel = stats.funnel || {};
    const topSales = stats.topSales || [];
    const queueOldestDays = queue.stages.reduce((n, stage) => Math.max(n, stage.oldestDays || 0), 0);
    const overdueLoans = (risk.buckets || [])
        .filter((b) => b.bucket !== 'Current')
        .reduce((n, b) => n + (b.loans || 0), 0);
    const riskScale = Math.max(...(risk.buckets || []).map((b) => b.balance || 0), 1);
    const queueScale = Math.max(...queue.stages.map((stage) => stage.cases || 0), 1);

    const totalRecentLoanAmount = recentLoans.reduce((sum, loan) => sum + Number(loan.amount || 0), 0);
    const avgRecentLoanAmount = Number(disbursalMtd.avgTicket)
        || (recentLoans.length ? Math.round(totalRecentLoanAmount / recentLoans.length) : 0);
    const latestPayment = recentPayments[0] || null;
    const latestLoan = recentLoans[0] || null;
    const dueSoonCount = recentLoans.filter((loan) => {
        if (!loan.dueDateRaw) return false;
        const dueDate = new Date(loan.dueDateRaw);
        if (Number.isNaN(dueDate.getTime())) return false;
        const diffDays = Math.ceil((dueDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
        return diffDays >= 0 && diffDays <= 7;
    }).length;
    const paymentMethodBreakdown = recentPayments.reduce((acc, payment) => {
        const method = String(payment.paymentMethod || 'unknown').toLowerCase();
        acc[method] = (acc[method] || 0) + 1;
        return acc;
    }, {});
    const sortedPaymentMethods = Object.entries(paymentMethodBreakdown)
        .map(([method, count]) => ({ method, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 3);

    // The old "disbursement trend" plotted the last few loan amounts next to
    // each other, which is not a time series — a run of equal-sized loans drew
    // a flat line. This is the real per-day disbursal the server aggregates.
    const recentLoanChartSeries = dashboardSeries.map((point) => ({
        ...point, disbursedAt: point.dateLabel, clientName: ''
    }));
    const trendAmount = dashboardSeries.reduce((sum, point) => sum + point.amount, 0);
    const trendLoans = dashboardSeries.reduce((sum, point) => sum + point.loans, 0);
    const trendSelection = dashboardSeries.find(point => point.id === selectedDisbursalPoint);
    const queueValue = queue.stages.reduce((sum, stage) => sum + Number(stage.value || 0), 0);
    const severeBalance = (risk.buckets || []).filter(bucket => ['31-60 Days', '61-90 Days', '90+ Days'].includes(bucket.bucket))
        .reduce((sum, bucket) => sum + Number(bucket.balance || 0), 0);
    const targetAmount = Number(stats.monthlyTarget) || 0;
    const targetProgress = targetAmount > 0 ? Math.round(Number(disbursalMtd.gross || 0) / targetAmount * 100) : null;
    // Achieved-vs-target as a meter instead of just the gap number: the fill is
    // "how much happened", a marker line is "how much was the target", scaled
    // to whichever of the two is bigger (plus headroom) so the marker isn't
    // glued to the bar's right edge when the target hasn't been hit yet, and
    // isn't pinned off-screen when it's been beaten.
    const disbursedMtdAmount = Number(disbursalMtd.gross || 0);
    const targetMeterScale = Math.max(disbursedMtdAmount, targetAmount, 1) * 1.15;
    const targetMeterFillPct = Math.min((disbursedMtdAmount / targetMeterScale) * 100, 100);
    const targetMeterMarkerPct = Math.min((targetAmount / targetMeterScale) * 100, 100);
    const targetMet = targetAmount > 0 && disbursedMtdAmount >= targetAmount;
    const recentLoanChartMax = Math.max(...recentLoanChartSeries.map((loan) => Number(loan.amount || 0)), 1);
    const recentLoanChartPoints = recentLoanChartSeries.map((loan, index) => {
        const x = recentLoanChartSeries.length === 1 ? 50 : (index / (recentLoanChartSeries.length - 1)) * 100;
        const y = 100 - ((Number(loan.amount || 0) / recentLoanChartMax) * 100);
        return {
            ...loan,
            x,
            y: Number.isFinite(y) ? y : 100,
            tooltipLabel: loan.loans != null
                ? `${loan.disbursedAt} · ${loan.loans} loan${loan.loans === 1 ? '' : 's'}`
                : (loan.disbursedAt || loan.clientName || `Day ${index + 1}`)
        };
    });
    const recentLoanChartLine = recentLoanChartPoints.map((point) => `${point.x},${point.y}`).join(' ');
    const recentLoanChartArea = recentLoanChartPoints.length
        ? `0,100 ${recentLoanChartLine} 100,100`
        : '0,100 100,100';

    // The collection-side twin of the disbursal chart above — same shape,
    // same timeframe toggle, sourced from stats.collectionTrend instead.
    const collectionSeries = Array.isArray(stats.collectionTrend)
        ? buildDashboardTrend(stats.collectionTrend, timeframe === '30d' ? 30 : 7, dashboardUpdatedAt || new Date())
        : [];
    const collectionTrendAmount = collectionSeries.reduce((sum, point) => sum + point.amount, 0);
    const collectionTrendCount = collectionSeries.reduce((sum, point) => sum + point.loans, 0);
    const collectionSelection = collectionSeries.find(point => point.id === selectedCollectionPoint);
    const collectionChartSeries = collectionSeries.map((point) => ({
        ...point, collectedAt: point.dateLabel
    }));
    const collectionChartMax = Math.max(...collectionChartSeries.map((point) => Number(point.amount || 0)), 1);
    const collectionChartPoints = collectionChartSeries.map((point, index) => {
        const x = collectionChartSeries.length === 1 ? 50 : (index / (collectionChartSeries.length - 1)) * 100;
        const y = 100 - ((Number(point.amount || 0) / collectionChartMax) * 100);
        return {
            ...point,
            x,
            y: Number.isFinite(y) ? y : 100,
            tooltipLabel: point.loans != null
                ? `${point.collectedAt} · ${point.loans} payment${point.loans === 1 ? '' : 's'}`
                : point.collectedAt
        };
    });
    const collectionChartLine = collectionChartPoints.map((point) => `${point.x},${point.y}`).join(' ');
    const collectionChartArea = collectionChartPoints.length
        ? `0,100 ${collectionChartLine} 100,100`
        : '0,100 100,100';

    useEffect(() => {
        setSelectedDisbursalPoint(null);
        setSelectedCollectionPoint(null);
    }, [timeframe, totalDisbursedAmount, stats.activeLoans]);

    const downloadDashboardCsv = (detailed = false) => {
        const rows = dashboardSeries.map((point) => ({
            date: point.fullDate,
            disbursed_loans: point.loans,
            disbursed_amount: point.amount,
            avg_ticket: point.avgTicket,
            ...(detailed ? {
                pending_applications: stats.pendingLoans || 0,
                total_users: stats.totalUsers || 0
            } : {})
        }));

        const headers = Object.keys(rows[0] || { date: '', disbursed_loans: '', disbursed_amount: '', avg_ticket: '' });
        const csv = [
            headers.join(','),
            ...rows.map((row) => headers.map((header) => row[header]).join(','))
        ].join('\n');

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = detailed ? 'dashboard-detailed.csv' : 'dashboard-summary.csv';
        link.click();
        URL.revokeObjectURL(url);
    };

    const showConfirm = (title, message, onConfirm) => {
        setModalConfig({
            show: true,
            type: 'confirm',
            title,
            message,
            onConfirm,
            confirmText: 'Confirm',
            cancelText: 'Cancel'
        });
    };

    const showAlert = (title, message) => {
        setModalConfig({
            show: true,
            type: 'alert',
            title,
            message,
            onConfirm: null,
            confirmText: 'OK',
            cancelText: ''
        });
    };

    const closeModal = () => {
        setModalConfig(prev => ({ ...prev, show: false }));
    };
    // Always exclude admin users from User Management page (show only regular client users)
    const includeAdminsInUsers = false;

    const [editingAdmin, setEditingAdmin] = useState(null);
    const [editingUser, setEditingUser] = useState(null);

    useEffect(() => {
        // Check for impersonation token in URL
        const urlParams = new URLSearchParams(window.location.search);
        const impersonateToken = urlParams.get('impersonate');

        if (impersonateToken) {
            try {
                // Decode the token
                const tokenData = JSON.parse(atob(impersonateToken));
                const { session, timestamp } = tokenData;

                // Check if token is not expired (valid for 5 minutes)
                const tokenAge = Date.now() - timestamp;
                if (tokenAge > 5 * 60 * 1000) {
                    showAlert('⚠️ Token Expired', 'The impersonation link has timed out for your security. Please generate a new one.');
                    // Remove token from URL
                    urlParams.delete('impersonate');
                    window.history.replaceState({}, '', `${window.location.pathname}?${urlParams.toString()}`);
                } else {
                    // Apply the impersonated session
                    const sessionKey = session.isAdmin ? 'adminSession' : 'userSession';
                    localStorage.setItem(sessionKey, JSON.stringify(session));

                    console.log(`🔌 Impersonated as: ${session.name}`);
                    console.log('Session:', session);

                    // Remove token from URL and reload
                    urlParams.delete('impersonate');
                    const newUrl = urlParams.toString()
                        ? `${window.location.pathname}?${urlParams.toString()}`
                        : window.location.pathname;
                    window.history.replaceState({}, '', newUrl);
                    window.location.reload();
                    return;
                }
            } catch (error) {
                console.error('Error applying impersonation token:', error);
                showAlert('❌ Link Compromised', 'The security token provided is invalid or has been tampered with.');
            }
        }

        const session = localStorage.getItem('adminSession');
        if (session) {
            const parsed = getStoredAdminSession();
            if (!parsed) {
                navigate('/admin/login');
                return;
            }
            setAdminSession(parsed);
            // Connect WebSocket for real-time updates and register named handlers (so we can remove on cleanup)
            wsService.connect(parsed.userId, parsed.role === 'super_admin')
                .then(() => {
                    // named handlers
                    const handleUserUpdated = () => refreshDataRef.current();
                    const handleUserDeleted = () => refreshDataRef.current();
                    const handleAdminCreated = () => refreshDataRef.current();
                    const handleAdminUpdated = (payload) => {
                        // Refresh dashboard lists for everyone
                        refreshDataRef.current();

                        // If the updated admin is ME, refresh my own session permissions instantly
                        const currentSession = getStoredAdminSession();
                        if (payload && payload.adminId === currentSession?.userId) {
                            console.log('⚡ Instant Permission Sync: Updating local session');
                            const updatedSession = {
                                ...currentSession,
                                permissions: payload.permissions || currentSession.permissions,
                                role: payload.role || currentSession.role,
                                level: payload.level || currentSession.level,
                                name: payload.name || currentSession.name
                            };
                            localStorage.setItem('adminSession', JSON.stringify(updatedSession));
                            setAdminSession(updatedSession);

                            // Optional: Show a subtle notification
                            showAlert('⚡ Security Update', 'Your administrative permissions have been updated by the system core.');
                        }
                    };
                    const handleAdminDeleted = () => refreshDataRef.current();
                    const handleLoanUpdated = () => refreshDataRef.current();
                    const handleTicketUpdated = () => refreshDataRef.current();
                    const handleStatsUpdated = () => {
                        setDashboardRefreshSignal((value) => value + 1);
                        refreshDataRef.current();
                    };

                    wsService.on('user.updated', handleUserUpdated);
                    wsService.on('user.deleted', handleUserDeleted);
                    wsService.on('admin.created', handleAdminCreated);
                    wsService.on('admin.updated', handleAdminUpdated);
                    wsService.on('admin.deleted', handleAdminDeleted);
                    wsService.on('loan.updated', handleLoanUpdated);
                    wsService.on('ticket.updated', handleTicketUpdated);
                    wsService.on('stats.updated', handleStatsUpdated);

                    // store handlers on the service so cleanup can reference them
                    wsService._handlers = {
                        handleUserUpdated,
                        handleUserDeleted,
                        handleAdminCreated,
                        handleAdminUpdated,
                        handleAdminDeleted,
                        handleLoanUpdated,
                        handleTicketUpdated,
                        handleStatsUpdated
                    };
                })
                .catch(err => console.error('WebSocket connect error:', err));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    dataViewRef.current = { activeTab, userSearch, usersPage, includeAdminsInUsers };
    useEffect(() => {
        const timer = setTimeout(() => refreshDataRef.current(), activeTab === 'users' ? 300 : 0);
        return () => {
            clearTimeout(timer);
            fetchQueuedRef.current = false;
            dashboardAbortRef.current?.abort();
        };
    }, [activeTab, userSearch, usersPage, includeAdminsInUsers]);

    // Cleanup websocket listeners on unmount
    useEffect(() => {
        return () => {
            if (wsService._handlers) {
                const h = wsService._handlers;
                wsService.off('user.updated', h.handleUserUpdated);
                wsService.off('user.deleted', h.handleUserDeleted);
                wsService.off('admin.created', h.handleAdminCreated);
                wsService.off('admin.updated', h.handleAdminUpdated);
                wsService.off('admin.deleted', h.handleAdminDeleted);
                wsService.off('loan.updated', h.handleLoanUpdated);
                wsService.off('ticket.updated', h.handleTicketUpdated);
                wsService.off('stats.updated', h.handleStatsUpdated);
                delete wsService._handlers;
            }
            try { wsService.disconnect(); } catch (e) { }
        };
    }, []);

    const fetchAllData = async () => {
        if (fetchInFlightRef.current) {
            fetchQueuedRef.current = true;
            return;
        }

        fetchInFlightRef.current = true;
        setIsLoading(true);
        const controller = new AbortController();
        dashboardAbortRef.current = controller;

        const fetchJsonSafe = async (url, options = {}) => {
            try {
                const response = await fetch(url, { ...options, signal: controller.signal });
                const data = await response.json().catch(() => null);
                return { ok: response.ok, status: response.status, data };
            } catch (error) {
                return { ok: false, error };
            }
        };

        try {
            const { activeTab: view, userSearch, usersPage, includeAdminsInUsers } = dataViewRef.current;
            const isOverview = view === 'dashboard' || view === 'overview';
            const adminSessionObj = getStoredAdminSession();
            const userId = adminSessionObj?.userId;
            const headers = userId ? { 'x-user-id': userId } : {};
            const canViewLeads = adminSessionObj && (
                adminSessionObj.role === 'sales' ||
                adminSessionObj.role === 'branch' ||
                adminSessionObj.role === 'collection' ||
                adminSessionObj.role === 'super_admin' ||
                adminSessionObj.role === 'admin' ||
                adminSessionObj.permissions?.includes?.('leads.view')
            );

            const tasks = [];
            const request = (enabled, path, applyResult) => {
                if (enabled) tasks.push(fetchJsonSafe(`${API_CONFIG.BASE_URL}${path}`, { headers }).then(result => {
                    if (!controller.signal.aborted) applyResult(result);
                }));
            };
            // Publish each section as it arrives; unrelated tabs do not load dashboard data.
            request(isOverview, '/api/admin/stats', (statsResult) => {
                const statsData = statsResult?.data;
                if (statsResult.ok && statsData?.success && statsData.data) {
                    setDashboardError('');
                    setDashboardUpdatedAt(new Date());
                    setStats({
                        ...statsData.data,
                        systemHealth: 'Excellent',
                        pendingApprovals: statsData.data.pendingLoans || 0,
                        totalAdmins: statsData.data.totalAdmins || 0
                    });
                    setRecentPayments(Array.isArray(statsData.recentPayments) ? statsData.recentPayments : []);
                } else {
                    setDashboardError('Dashboard data could not be refreshed. Please retry.');
                }
                setIsLoading(false);
            });
            request(view === 'admins', '/api/admin/admins', (adminsResult) => {
                const adminsData = adminsResult?.data;
                if (adminsData?.success) {
                    const formattedAdmins = adminsData.data.map(a => ({
                        id: a.id,
                        name: [a.first_name, a.last_name].filter(n => n && n !== 'null').join(' ').trim() || 'Unknown Admin',
                        email: a.email,
                        phone: a.phone,
                        role: a.role_name,
                        level: a.level,
                        permissionCount: Array.isArray(a.permissions) ? a.permissions.length : 0,
                        status: a.is_active ? 'Active' : 'Inactive',
                        lastLoginAt: a.last_login_at || null,
                        lastLogin: a.last_login_at ? new Date(a.last_login_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : 'Never',
                        createdAt: a.created_at || null
                    }));
                    setAdmins(formattedAdmins);
                    setStats(prev => ({ ...prev, totalAdmins: Math.max(prev.totalAdmins || 0, formattedAdmins.length) }));
                }
            });
            request(view === 'users', `/api/admin/users?limit=20&page=${usersPage}&includeAdmins=${includeAdminsInUsers}&search=${encodeURIComponent(userSearch || '')}`, (usersResult) => {
                if (view === 'users' && (dataViewRef.current.userSearch !== userSearch || dataViewRef.current.usersPage !== usersPage || dataViewRef.current.includeAdminsInUsers !== includeAdminsInUsers)) return;
                const usersData = usersResult?.data;
                if (usersData?.success) {
                    const formattedUsers = usersData.data.users.map(u => ({
                        id: u.id,
                        name: u.full_name || [u.first_name, u.last_name].filter(n => n && n !== 'null').join(' ').trim() || 'Unknown User',
                        email: u.email || '-',
                        phone: u.phone,
                        status: u.is_active ? 'Active' : 'Inactive',
                        kyc: u.kyc_status,
                        loans: 0,
                        joinDate: new Date(u.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })
                    }));
                    setRecentUsers(formattedUsers);
                    setUsersTotalPages(usersData.data.totalPages || 1);
                }
            });
            request(isOverview, '/api/admin/loans?limit=5&include_summary=0&fast=1', (loansResult) => {
                const loansData = loansResult?.data;
                if (loansData?.success && Array.isArray(loansData.data?.loans)) {
                    const formattedLoans = loansData.data.loans.map((loan) => ({
                        id: loan.id,
                        clientName: loan.full_name || [loan.first_name, loan.last_name].filter(n => n && n !== 'null').join(' ').trim() || 'Unknown',
                        loanType: loan.loan_type || loan.type || 'Loan',
                        amount: loan.amount || 0,
                        status: loan.status || 'Unknown',
                        accountNumber: loan.account_number || loan.accountNumber || '—',
                        disbursedAtRaw: loan.disbursed_at || null,
                        dueDateRaw: loan.due_date || null,
                        disbursedAt: formatDisplayDate(loan.disbursed_at),
                        dueDate: formatDisplayDate(loan.due_date)
                    }));
                    setRecentLoans(formattedLoans);
                }
            });
            request(view === 'logs', '/api/admin/activity', (logsResult) => {
                const logsData = logsResult?.data;
                if (logsData?.success && Array.isArray(logsData.data)) {
                    const formattedLogs = logsData.data.map(l => ({
                        id: l.id,
                        action: l.action_type || 'activity',
                        user: l.admin_name || l.admin_email || 'Unknown',
                        details: l.description || '',
                        time: l.created_at,
                        severity: (l.severity || 'info').toLowerCase(),
                        target: l.target_type ? `${l.target_type} #${l.target_id || ''}`.trim() : ''
                    }));
                    setSystemLogs(formattedLogs);
                }
            });
            request(view === 'overview' && canViewLeads, '/api/admin/leads?summary=1', (leadsResult) => {
                const leadsData = leadsResult?.data;
                if (leadsData?.success && leadsData.data) {
                    setLeadSummary(leadsData.data);
                }
            });
            await Promise.all(tasks);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            setDashboardError('Dashboard data could not be refreshed. Please retry.');
        } finally {
            fetchInFlightRef.current = false;
            setIsLoading(false);
            if (fetchQueuedRef.current) {
                fetchQueuedRef.current = false;
                void refreshDataRef.current();
            }
        }
    };
    refreshDataRef.current = fetchAllData;

    // Cleanup WebSocket on unmount
    useEffect(() => {
        return () => {
            try {
                wsService.disconnect();
            } catch (e) {
                // ignore
            }
        };
    }, []);

    const handleEditAdmin = async (admin) => {
        setEditingAdmin(admin);
        setShowEditAdminModal(true);
    };

    const handleDeleteAdmin = (adminId) => {
        showConfirm(
            'Delete Admin Account',
            'Are you sure you want to delete this admin? This action cannot be undone and will un-link all associated records.',
            async () => {
                try {
                    const adminSessionObj = getStoredAdminSession();
                    const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/admins/${adminId}`, {
                        method: 'DELETE',
                        headers: { 'x-user-id': adminSessionObj?.userId }
                    });
                    const data = await res.json();
                    if (data.success) {
                        showAlert('Success', 'Admin account has been permanently removed.');
                        fetchAllData();
                    } else {
                        showAlert('Error', data.message || 'Failed to delete admin');
                    }
                } catch (error) {
                    showAlert('Server Error', 'Failed to connect to the security core. Please check your connection.');
                }
            }
        );
    };

    const toggleAdminStatus = (adminId, currentStatus) => {
        const activating = currentStatus !== 'Active';
        showConfirm(
            activating ? 'Activate Admin' : 'Deactivate Admin',
            activating
                ? 'Re-enable this admin account? They will be able to sign in again.'
                : 'Deactivate this admin account? They will be signed out and blocked from logging in.',
            async () => {
                try {
                    const adminSessionObj = getStoredAdminSession();
                    const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/admins/${adminId}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json', 'x-user-id': adminSessionObj?.userId },
                        body: JSON.stringify({ is_active: activating })
                    });
                    const data = await res.json();
                    if (data.success) fetchAllData();
                    else showAlert('Status Update Failed', data.message || 'Could not update admin status');
                } catch (error) {
                    showAlert('Connection Error', 'Failed to reach the server.');
                }
            }
        );
    };

    const generateTempPassword = () => {
        const upper = 'ABCDEFGHJKMNPQRSTUVWXYZ';
        const lower = 'abcdefghijkmnpqrstuvwxyz';
        const digits = '23456789';
        const symbols = '@#$%&*';
        const all = upper + lower + digits + symbols;
        const pick = (set) => set[Math.floor(Math.random() * set.length)];
        // guarantee one of each class, then fill to 12 chars, then shuffle
        let chars = [pick(upper), pick(lower), pick(digits), pick(symbols)];
        while (chars.length < 12) chars.push(pick(all));
        for (let i = chars.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [chars[i], chars[j]] = [chars[j], chars[i]];
        }
        return chars.join('');
    };

    const handleResetAdminPassword = (admin) => {
        showConfirm(
            'Reset Password',
            `Generate a new temporary password for ${admin.name}? Their current password will stop working immediately.`,
            async () => {
                try {
                    const adminSessionObj = getStoredAdminSession();
                    const tempPassword = generateTempPassword();
                    const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/users/${admin.id}/reset-password`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'x-user-id': adminSessionObj?.userId },
                        body: JSON.stringify({ newPassword: tempPassword })
                    });
                    const data = await res.json();
                    if (data.success) {
                        showAlert(
                            'Password Reset',
                            `Temporary password for ${admin.name}:\n\n${tempPassword}\n\nShare it securely — it won't be shown again.`
                        );
                    } else {
                        showAlert('Reset Failed', data.message || 'Could not reset password');
                    }
                } catch (error) {
                    showAlert('Connection Error', 'Failed to reach the server.');
                }
            }
        );
    };

    const handleViewUser = (user) => {
        // Use the canonical routed customer screen. The former dashboard
        // modal duplicated the customer feature set, so fixes added to one
        // screen could silently be missing from the other.
        if (user?.id) navigate(`/admin/customer/${user.id}`);
    };



    const changeUserStatus = async (userId, val) => {
        if (!val) return;
        const normalized = val.charAt(0).toUpperCase() + val.slice(1).toLowerCase();
        // Optimistic UI update
        setRecentUsers(prev => prev.map(u => u.id === userId ? { ...u, status: normalized } : u));
        try {
            const adminSessionObj = getStoredAdminSession();
            if (normalized === 'Restricted') {
                const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/users/${userId}/restrict`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'x-user-id': adminSessionObj?.userId },
                    body: JSON.stringify({ restricted: true })
                });
                const data = await res.json();
                if (!data.success) {
                    fetchAllData();
                    showAlert('Action Failed', data.message || 'Failed to restrict user');
                }
            } else {
                const isActive = normalized === 'Active';
                const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/users/${userId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', 'x-user-id': adminSessionObj?.userId },
                    body: JSON.stringify({ is_active: isActive })
                });
                const data = await res.json();
                if (!data.success) {
                    fetchAllData();
                    showAlert('Status Update Failed', data.message || 'Failed to update user status');
                }
            }
        } catch (e) {
            fetchAllData();
            showAlert('Connection Error', 'System unreachable.');
        }
    };



    const handleEditUser = async (user) => {
        setEditingUser(user);
        setShowEditUserModal(true);
    };

    const handleImpersonate = (targetId, type = 'admin') => {
        showConfirm(
            'Secure Session Impersonation',
            `You are about to establish a secondary neural link as this ${type}. Do you wish to proceed?`,
            async () => {
                try {
                    const adminSessionObj = getStoredAdminSession();
                    const hasImpersonatePerm = adminSessionObj?.role === 'super_admin' || adminSessionObj?.permissions?.includes('admin.impersonate');

                    if (!adminSessionObj || !hasImpersonatePerm || !adminSessionObj.userId) {
                        showAlert('Access Denied', 'Permission admin.impersonate required for neural impersonation.');
                        return;
                    }
                    const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/impersonate/${targetId}`, {
                        method: 'POST',
                        headers: {
                            'x-user-id': adminSessionObj?.userId,
                            'Content-Type': 'application/json'
                        }
                    });
                    const data = await res.json();

                    if (data.success) {
                        // Create session object with timestamps
                        const impersonatedSession = {
                            ...data.data,
                            loginTime: Date.now(),
                            expiresAt: Date.now() + (60 * 60 * 1000), // 1 hour session
                            isImpersonating: true,
                            originalAdminId: adminSessionObj?.userId
                        };

                        // Create a unique token for this impersonation session
                        const impersonationToken = btoa(JSON.stringify({
                            session: impersonatedSession,
                            timestamp: Date.now()
                        }));

                        // Determine the target URL
                        let targetUrl;
                        if (data.data.isAdmin) {
                            targetUrl = `/admin/dashboard?impersonate=${impersonationToken}`;
                            const fullUrl = window.location.origin + targetUrl;
                            window.open(fullUrl, '_blank');
                        } else {
                            // Regular user portal is on port 3000 locally
                            const isLoc = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
                            const clientPortalUrl = isLoc ? 'http://localhost:3000' : window.location.origin.replace('admin.', '');
                            targetUrl = `${clientPortalUrl}/dashboard?impersonate=${impersonationToken}`;
                            window.open(targetUrl, '_blank');
                        }

                        showAlert('Connection Established', `Neural link to ${data.data.name} active in new sector.`);
                    } else {
                        showAlert('Impersonation Failed', data.message || 'Identity verification rejected.');
                    }
                } catch (error) {
                    console.error('Impersonation error:', error);
                    showAlert('Critical Error', 'Signal lost during transmission.');
                }
            }
        );
    };

    const handleDeleteUser = (userId) => {
        showConfirm(
            'Delete User Record',
            'Are you sure you want to permanently delete this user? This will erase all history, loans, and personal data.',
            async () => {
                try {
                    const adminSessionObj = getStoredAdminSession();
                    const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/users/${userId}`, {
                        method: 'DELETE',
                        headers: { 'x-user-id': adminSessionObj?.userId }
                    });
                    const data = await res.json();
                    if (data.success) {
                        showAlert('User Deleted', 'Account and associated data moved to void.');
                        fetchAllData();
                    } else {
                        showAlert('Error', data.message || 'User cleanup failed.');
                    }
                } catch (error) {
                    showAlert('Critical Error', 'Database unreachable.');
                }
            }
        );
    };

    /*
    const getStatusClass = (status) => {
        switch (status.toLowerCase()) {
            case 'active':
            case 'approved':
            case 'verified':
            case 'excellent':
                return 'status-success';
            case 'pending':
                return 'status-warning';
            case 'inactive':
            case 'rejected':
                return 'status-danger';
            default:
                return '';
        }
    };
    */

    const getSeverityClass = (severity) => {
        switch (severity) {
            case 'success':
                return 'log-success';
            case 'warning':
                return 'log-warning';
            case 'error':
                return 'log-error';
            default:
                return 'log-info';
        }
    };

    // One badge colour per role. This used to be a hardcoded switch that
    // predated the credit_head/credit_executive/collection_head/etc. roles
    // (staff_targets' ROLE_METRIC_MAP), so any of those fell through to the
    // `default` case and rendered the raw DB value verbatim — text-transform
    // then uppercased the underscore too, e.g. "COLLECTION_EXECUTIVE". Now
    // every known role gets a real label (via prettyRoleName, already used
    // by the role filter dropdown below) and a colour from the same
    // department, "head" a shade darker than "executive".
    const ROLE_BADGE_CLASS = {
        super_admin: 'super-admin',
        admin: 'admin',
        credit_head: 'credit-head',
        credit_executive: 'credit-exec',
        collection_head: 'collection-head',
        collection_executive: 'collection-exec',
        sales_head: 'sales-head',
        sales_executive: 'sales-exec',
        support: 'support',
        branch: 'branch',
        accounts: 'accounts',
        investor: 'investor'
    };
    const getRoleBadge = (role) => {
        if (!role) return <span className="role-dot-badge"><span className="role-dot user" />User</span>;
        const cls = ROLE_BADGE_CLASS[role.toLowerCase()] || 'user';
        return <span className="role-dot-badge"><span className={`role-dot ${cls}`} />{prettyRoleName(role)}</span>;
    };

    const handleAdminCreated = (newAdmin) => {
        fetchAllData();
    };

    const handleUserCreated = (newUser) => {
        fetchAllData();
    };

    // ── Admins tab: derived filters + summary ──
    const adminRoleOptions = Array.from(new Set(admins.map(a => a.role).filter(Boolean)));
    const filteredAdmins = admins.filter(a => {
        const q = adminSearch.trim().toLowerCase();
        const matchesSearch = !q
            || a.name.toLowerCase().includes(q)
            || (a.email || '').toLowerCase().includes(q)
            || (a.phone || '').toLowerCase().includes(q);
        const matchesRole = adminRoleFilter === 'all' || a.role === adminRoleFilter;
        const matchesStatus = adminStatusFilter === 'all' || a.status === adminStatusFilter;
        return matchesSearch && matchesRole && matchesStatus;
    });
    const adminSummary = {
        total: admins.length,
        active: admins.filter(a => a.status === 'Active').length,
        inactive: admins.filter(a => a.status !== 'Active').length,
        roles: adminRoleOptions.length
    };
    const adminInitials = (name) => (name || '?').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
    const prettyRoleName = (role) => String(role || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    // How many of each role — e.g. "6 Super Admin, 10 Credit Executive,
    // 1 Investor" — so "who has how much access" is answerable at a glance
    // instead of counting badges down the table by eye.
    const adminRoleBreakdown = Object.entries(
        admins.reduce((acc, a) => {
            const key = (a.role || 'user').toLowerCase();
            acc[key] = (acc[key] || 0) + 1;
            return acc;
        }, {})
    )
        .map(([role, count]) => ({ role, count, cls: ROLE_BADGE_CLASS[role] || 'user', label: prettyRoleName(role) }))
        .sort((a, b) => b.count - a.count);
    const relLastLogin = (v) => {
        if (!v) return 'Never';
        const d = new Date(v);
        if (isNaN(d)) return 'Never';
        const mins = Math.floor((Date.now() - d.getTime()) / 60000);
        if (mins < 1) return 'Just now';
        if (mins < 60) return `${mins}m ago`;
        if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
        if (mins < 43200) return `${Math.floor(mins / 1440)}d ago`;
        return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
    };

    return (
        <div className="super-admin-dashboard-page">
            {/* Tab Content */}
            <div className="super-tab-content">
                {activeTab === 'dashboard' && (
                    <div className="dash">
                        <DashboardBanner adminSession={adminSession} />
                        <section className="dash-header">
                            <div className="dash-header-copy">
                                <p className="dash-eyebrow">Loan Operations</p>
                                <h3 className="dash-title">Welcome back, {firstName}</h3>
                                <p className="dash-subtitle">Here’s what’s happening across your lending portfolio today.</p>
                            </div>
                            <div className="dash-header-actions">
                                <span className="dash-health" role="status">
                                    {isLoading ? 'Refreshing…' : dashboardUpdatedAt ? `Updated ${dashboardUpdatedAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })} IST${dashboardError ? ' · stale' : ''}` : 'Awaiting data'}
                                </span>
                                <button type="button" className="dash-btn ghost" disabled={isLoading} onClick={fetchAllData}>
                                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" /><path d="M8 16H3v5" /></svg>
                                    Refresh
                                </button>
                                <button type="button" className="dash-btn primary" disabled={!dashboardSeries.length || isLoading} onClick={() => downloadDashboardCsv(true)}>
                                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" x2="12" y1="15" y2="3" /></svg>
                                    Export CSV
                                </button>
                            </div>
                        </section>

                        {dashboardError && <div className="dash-data-notice" role="alert">{dashboardError} {dashboardUpdatedAt ? 'Showing the last successful snapshot.' : ''}</div>}
                        {!dashboardUpdatedAt && <div className="dash-data-notice" role="status">{isLoading ? 'Loading portfolio, collections and disbursement data…' : 'Dashboard metrics are unavailable.'}</div>}
                        {dashboardUpdatedAt && <>
                        <section className="dash-insights" aria-label="Operational priorities">
                            <div className="dash-insight-intro"><p className="dash-panel-eyebrow">Focus today</p><h4>Portfolio priorities</h4><span>Current book &amp; pending cases</span></div>
                            <div><span>Pending decision value</span><strong>{formatCurrency(queueValue)}</strong><small>{queue.total || 0} cases · oldest {queueOldestDays} days</small></div>
                            <div><span>Outstanding · 31+ days overdue</span><strong>{formatCurrency(severeBalance)}</strong><small>{portfolio.balance ? (severeBalance / portfolio.balance * 100).toFixed(1) : '0'}% of outstanding balance</small></div>
                            <div>
                                <span>Monthly disbursal target gap</span>
                                <strong>{targetAmount ? formatCurrency(Math.max(targetAmount - disbursedMtdAmount, 0)) : 'Not configured'}</strong>
                                {targetAmount > 0 && (
                                    <div
                                        className={`dash-target-meter${targetMet ? ' met' : ''}`}
                                        role="img"
                                        aria-label={`${formatCurrency(disbursedMtdAmount)} disbursed of a ${formatCurrency(targetAmount)} target this month`}
                                        title={`${formatCurrency(disbursedMtdAmount)} achieved of ${formatCurrency(targetAmount)} target`}
                                    >
                                        <div className="dash-target-meter-track">
                                            <div className="dash-target-meter-fill" style={{ width: `${targetMeterFillPct}%` }} />
                                            <div className="dash-target-meter-marker" style={{ left: `${targetMeterMarkerPct}%` }} />
                                        </div>
                                    </div>
                                )}
                                <small>{targetAmount ? <>{targetProgress}% achieved — {formatCurrency(disbursedMtdAmount)} of {formatCurrency(targetAmount)}</> : 'Set a target to track progress'}</small>
                            </div>
                            {hasPermission(tabPermissions.loans) && <button type="button" className="dash-btn ghost" onClick={() => handleTabChange('loans')}>Open loans →</button>}
                        </section>
                        {/* Six numbers a lending desk acts on: what went out, what is
                            still owed, what is waiting on a human, what came back, what
                            it earned, and what is slipping. */}
                        <section className="dash-kpis">
                            <article className="dash-kpi" data-tone="indigo">
                                <div className="dash-kpi-top">
                                    <span className="dash-kpi-icon">
                                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 3h12" /><path d="M6 8h12" /><path d="m6 13 8.5 8" /><path d="M6 13h3" /><path d="M9 13c6.667 0 6.667-10 0-10" /></svg>
                                    </span>
                                    <span className="dash-kpi-chip up">{funnel.disbursalRate || 0}% of applications</span>
                                </div>
                                <span className="dash-kpi-label">Disbursed this month</span>
                                <strong className="dash-kpi-value">{stats.totalDisbursed || '₹0'}</strong>
                                <span className="dash-kpi-subvalue">
                                    Net {formatCurrency(stats.disbursal?.mtd?.net || 0)} after processing fee &amp; GST
                                </span>
                                <span className="dash-kpi-foot">
                                    {stats.disbursedLoans || 0} loans · {formatCurrency(avgRecentLoanAmount)} average ticket
                                </span>
                            </article>

                            <article className="dash-kpi" data-tone="sky">
                                <div className="dash-kpi-top">
                                    <span className="dash-kpi-icon">
                                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 6v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2H5a2 2 0 0 1 0-4h13" /><circle cx="17" cy="13.5" r="1.2" /></svg>
                                    </span>
                                    <span className="dash-kpi-chip neutral">{portfolio.liveLoans || 0} live loans</span>
                                </div>
                                <span className="dash-kpi-label">Still to be repaid</span>
                                <strong className="dash-kpi-value">{formatCurrency(portfolio.balance || 0)}</strong>
                                <span className="dash-kpi-foot">
                                    {formatCurrency(portfolio.collected || 0)} of {formatCurrency(portfolio.expectedRepayment || 0)} recovered
                                </span>
                            </article>

                            <article className="dash-kpi" data-tone="amber">
                                <div className="dash-kpi-top">
                                    <span className="dash-kpi-icon">
                                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
                                    </span>
                                    {queueOldestDays > 0 && (
                                        <span className="dash-kpi-chip warn">oldest {queueOldestDays}d</span>
                                    )}
                                </div>
                                <span className="dash-kpi-label">Waiting on a decision</span>
                                <strong className="dash-kpi-value">{(queue.total || 0).toLocaleString('en-IN')}</strong>
                                <span className="dash-kpi-foot">
                                    Across {queue.stages.filter((stage) => stage.cases > 0).length} review stages
                                </span>
                            </article>

                            <article className="dash-kpi" data-tone="emerald">
                                <div className="dash-kpi-top">
                                    <span className="dash-kpi-icon">
                                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m3 17 6-6 4 4 8-8" /><path d="M21 7v5h-5" /></svg>
                                    </span>
                                    <span className={`dash-kpi-chip ${(collections.efficiency || 0) >= 80 ? 'up' : 'warn'}`}>
                                        {collections.settledCount || 0} closed
                                    </span>
                                </div>
                                <span className="dash-kpi-label">Collection efficiency</span>
                                <strong className="dash-kpi-value">{collections.efficiency || 0}%</strong>
                                <span className="dash-kpi-foot">
                                    {formatCurrency(collections.collectedAmount || 0)} of {formatCurrency(collections.dueAmount || 0)} due this month
                                </span>
                            </article>

                            <article className="dash-kpi" data-tone="violet">
                                <div className="dash-kpi-top">
                                    <span className="dash-kpi-icon">
                                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 2v20" /><path d="M17 6.5c0-2-2.2-3-5-3s-5 1-5 3 2.2 2.7 5 3.5 5 1.5 5 3.5-2.2 3-5 3-5-1-5-3" /></svg>
                                    </span>
                                    <span className="dash-kpi-chip up">MTD disbursals</span>
                                </div>
                                <span className="dash-kpi-label">Contracted fees & interest</span>
                                <strong className="dash-kpi-value">{formatCurrency(revenue.total || 0)}</strong>
                                <span className="dash-kpi-foot">
                                    {formatCurrency(revenue.processingFee || 0)} processing · {formatCurrency(revenue.interest || 0)} interest
                                </span>
                            </article>

                            <article className="dash-kpi" data-tone="teal">
                                <div className="dash-kpi-top">
                                    <span className="dash-kpi-icon">
                                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3 2.5 19.5h19L12 3Z" /><path d="M12 10v4" /><path d="M12 17.5h.01" /></svg>
                                    </span>
                                    <span className={`dash-kpi-chip ${overdueLoans ? 'warn' : 'up'}`}>
                                        {overdueLoans} overdue
                                    </span>
                                </div>
                                <span className="dash-kpi-label">Portfolio at risk</span>
                                <strong className="dash-kpi-value">{risk.parPercent || 0}%</strong>
                                <span className="dash-kpi-foot">
                                    {formatCurrency(risk.overdueBalance || 0)} past its due date
                                </span>
                            </article>

                        </section>

                        <section className="dash-panel dash-compare-panel" aria-label="Lifetime disbursed vs collected">
                            <header className="dash-panel-head">
                                <div>
                                    <p className="dash-panel-eyebrow">Since inception</p>
                                    <h4>Disbursed vs collected</h4>
                                </div>
                            </header>
                            <div className="dash-compare">
                                <div className="dash-compare-row">
                                    <div className="dash-compare-row-head">
                                        <span>Disbursed</span>
                                        <strong>{formatCurrency(lifetime.disbursedGross || 0)}</strong>
                                    </div>
                                    <div className="dash-compare-track">
                                        <span className="dash-compare-seg" style={{ width: '100%', background: '#4f46e5' }} />
                                    </div>
                                    <span className="dash-compare-caption">{(lifetime.disbursedLoans || 0).toLocaleString('en-IN')} loans disbursed lifetime</span>
                                </div>

                                <div className="dash-compare-row">
                                    <div className="dash-compare-row-head">
                                        <span>Collected</span>
                                        <strong>{formatCurrency(lifetime.collected || 0)}</strong>
                                    </div>
                                    <div className="dash-compare-track">
                                        {lifetime.disbursedGross > 0 && (
                                            <>
                                                <span className="dash-compare-seg" title={`Principal: ${formatCurrency(lifetime.collectedPrincipal || 0)}`} style={{ width: `${(lifetime.collectedPrincipal || 0) / lifetime.disbursedGross * 100}%`, background: '#0ea5e9' }} />
                                                <span className="dash-compare-seg" title={`Interest: ${formatCurrency(lifetime.collectedInterest || 0)}`} style={{ width: `${(lifetime.collectedInterest || 0) / lifetime.disbursedGross * 100}%`, background: '#16a34a' }} />
                                                <span className="dash-compare-seg" title={`Processing fee & GST: ${formatCurrency((lifetime.collectedProcessingFee || 0) + (lifetime.collectedGst || 0))}`} style={{ width: `${((lifetime.collectedProcessingFee || 0) + (lifetime.collectedGst || 0)) / lifetime.disbursedGross * 100}%`, background: '#f59e0b' }} />
                                            </>
                                        )}
                                    </div>
                                    <span className="dash-compare-caption">
                                        {lifetime.disbursedGross ? Math.round((lifetime.collected || 0) / lifetime.disbursedGross * 1000) / 10 : 0}% of lifetime disbursal recovered
                                    </span>
                                </div>

                                <div className="dash-compare-legend">
                                    <span className="dash-compare-legend-item"><i className="dash-compare-legend-dot" style={{ background: '#0ea5e9' }} />Principal <b>{formatCurrency(lifetime.collectedPrincipal || 0)}</b></span>
                                    <span className="dash-compare-legend-item"><i className="dash-compare-legend-dot" style={{ background: '#16a34a' }} />Interest <b>{formatCurrency(lifetime.collectedInterest || 0)}</b></span>
                                    <span className="dash-compare-legend-item"><i className="dash-compare-legend-dot" style={{ background: '#f59e0b' }} />Processing fee &amp; GST <b>{formatCurrency((lifetime.collectedProcessingFee || 0) + (lifetime.collectedGst || 0))}</b></span>
                                </div>
                            </div>
                        </section>

                        <section className="dash-ops">
                            {/* The backlog broken out by the stage each case is stuck in —
                                a single "pending" count hid three later review stages. */}
                            <article className="dash-panel">
                                <header className="dash-panel-head">
                                    <div>
                                        <p className="dash-panel-eyebrow">Approval queue</p>
                                        <h4>Where cases are waiting</h4>
                                    </div>
                                    <div className="dash-panel-metric">
                                        <strong>{queue.total || 0}</strong>
                                        <span>in queue</span>
                                    </div>
                                </header>
                                <ul className="ops-list">
                                    {queue.stages.map((stage) => (
                                        <li className="ops-row" key={stage.status}>
                                            <span className="ops-name">{stage.label}</span>
                                            <span className="ops-bar">
                                                <span
                                                    className="ops-bar-fill"
                                                    style={{ width: `${Math.round(((stage.cases || 0) / queueScale) * 100)}%` }}
                                                />
                                            </span>
                                            <span className="ops-value">
                                                <strong>{stage.cases}</strong>
                                                <em>{formatCurrency(stage.value)}</em>
                                            </span>
                                        </li>
                                    ))}
                                    {!queue.total && <li className="ops-empty">Nothing waiting — every case has a decision.</li>}
                                </ul>
                                {queueOldestDays > 0 && (
                                    <p className="ops-note">
                                        Longest wait: <strong>{queueOldestDays} days</strong>
                                    </p>
                                )}
                            </article>

                            <article className="dash-panel">
                                <header className="dash-panel-head">
                                    <div>
                                        <p className="dash-panel-eyebrow">Ageing</p>
                                        <h4>Book by days past due</h4>
                                    </div>
                                    <div className="dash-panel-metric">
                                        <strong>{risk.parPercent || 0}%</strong>
                                        <span>at risk</span>
                                    </div>
                                </header>
                                <ul className="ops-list">
                                    {(risk.buckets || []).map((bucket) => (
                                        <li className="ops-row" key={bucket.bucket}>
                                            <span className="ops-name">{bucket.bucket}</span>
                                            <span className="ops-bar">
                                                <span
                                                    className={`ops-bar-fill${bucket.bucket === 'Current' ? '' : ' is-risk'}`}
                                                    style={{ width: `${Math.round(((bucket.balance || 0) / riskScale) * 100)}%` }}
                                                />
                                            </span>
                                            <span className="ops-value">
                                                <strong>{bucket.loans}</strong>
                                                <em>{formatCurrency(bucket.balance)}</em>
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </article>

                            <article className="dash-panel">
                                <header className="dash-panel-head">
                                    <div>
                                        <p className="dash-panel-eyebrow">This month</p>
                                        <h4>Application funnel</h4>
                                    </div>
                                    <div className="dash-panel-metric">
                                        <strong>{funnel.approvalRate || 0}%</strong>
                                        <span>approved</span>
                                    </div>
                                </header>
                                <ol className="ops-funnel">
                                    {[
                                        { key: 'applied', label: 'Applied', value: funnel.applied || 0 },
                                        { key: 'approved', label: 'Approved', value: funnel.approved || 0 },
                                        { key: 'disbursed', label: 'Disbursed', value: funnel.disbursed || 0 },
                                        { key: 'rejected', label: 'Rejected', value: funnel.rejected || 0, muted: true }
                                    ].map((step) => (
                                        <li className={`ops-step${step.muted ? ' is-muted' : ''}`} key={step.key}>
                                            <span
                                                className="ops-step-bar"
                                                style={{ width: `${Math.round(((step.value || 0) / Math.max(funnel.applied || 1, 1)) * 100)}%` }}
                                            />
                                            <span className="ops-step-label">{step.label}</span>
                                            <span className="ops-step-value">{step.value}</span>
                                        </li>
                                    ))}
                                </ol>
                                {topSales.length > 0 && (
                                    <div className="ops-leaders">
                                        <p className="ops-leaders-head">Top sourcing this month</p>
                                        {topSales.slice(0, 3).map((person) => (
                                            <div className="ops-leader" key={person.name}>
                                                <span>{person.name}</span>
                                                <em>{formatCurrency(person.value)}</em>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </article>
                        </section>

                        <section className="dash-grid" aria-label="Lending activity">
                            <div className="dash-col-main">
                                <article className="dash-panel">
                                    <header className="dash-panel-head">
                                        <div>
                                            <p className="dash-panel-eyebrow">Disbursement trend</p>
                                            <h4>Disbursed per day</h4>
                                            <p className="dash-trend-caption">{dashboardWindowLabel} · {trendLoans} loans · {formatCurrency(trendAmount)}</p>
                                        </div>
                                        <div className="dash-panel-metric">
                                            <div className="dash-periods" role="group" aria-label="Disbursement trend period">
                                                {['7d', '30d'].map(period => <button type="button" key={period} aria-pressed={timeframe === period} onClick={() => setTimeframe(period)}>{period === '7d' ? '7 days' : '30 days'}</button>)}
                                            </div>
                                        </div>
                                    </header>

                                    <p className="dash-trend-detail" aria-live="polite">{trendSelection ? `${trendSelection.dateLabel}: ${formatCurrency(trendSelection.amount)} · ${trendSelection.loans} loans · ${formatCurrency(trendSelection.avgTicket)} average ticket` : trendAmount ? 'Select a chart point for daily details.' : 'No disbursements in this period.'}</p>
                                    <div className="dash-chart">
                                        <div className="dash-chart-axis">
                                            <span>{formatCurrency(recentLoanChartMax)}</span>
                                            <span>{formatCurrency(Math.round(recentLoanChartMax * 0.5))}</span>
                                            <span>₹0</span>
                                        </div>
                                        <div className="dash-chart-plot">
                                            <div className="dash-chart-canvas">
                                                <svg className="dash-chart-svg" viewBox="0 0 100 100" preserveAspectRatio="none" role="img" aria-label="Daily disbursal trend">
                                                    <defs>
                                                        <linearGradient id="dashTrendFill" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="0%" stopColor="rgba(79, 70, 229, 0.28)" />
                                                            <stop offset="100%" stopColor="rgba(79, 70, 229, 0.02)" />
                                                        </linearGradient>
                                                    </defs>
                                                    <line className="dash-chart-grid" x1="0" y1="25" x2="100" y2="25" />
                                                    <line className="dash-chart-grid" x1="0" y1="50" x2="100" y2="50" />
                                                    <line className="dash-chart-grid" x1="0" y1="75" x2="100" y2="75" />
                                                    <polygon points={recentLoanChartArea} fill="url(#dashTrendFill)" />
                                                    <polyline points={recentLoanChartLine} fill="none" stroke="#4f46e5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                                                </svg>
                                                <div className="dash-chart-points">
                                                    {recentLoanChartPoints.map((point) => (
                                                        <button type="button"
                                                            key={point.id}
                                                            className="dash-chart-point"
                                                            aria-label={`${point.tooltipLabel}: ${formatCurrency(point.amount)}`}
                                                            aria-pressed={selectedDisbursalPoint === point.id}
                                                            onClick={() => setSelectedDisbursalPoint(point.id)}
                                                            style={{ left: `${point.x}%`, top: `${point.y}%` }}
                                                            title={`${point.tooltipLabel}: ${formatCurrency(point.amount)}`}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="dash-chart-labels">
                                                {recentLoanChartPoints.filter((point, index) => index % Math.ceil(recentLoanChartPoints.length / 5) === 0 || index === recentLoanChartPoints.length - 1).map((point) => (
                                                    <span key={`${point.id}-lbl`}>{point.disbursedAt || point.clientName}</span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </article>
                                <article className="dash-panel">
                                    <header className="dash-panel-head">
                                        <div>
                                            <p className="dash-panel-eyebrow">Collection trend</p>
                                            <h4>Collected per day</h4>
                                            <p className="dash-trend-caption">{dashboardWindowLabel} · {collectionTrendCount} payments · {formatCurrency(collectionTrendAmount)}</p>
                                        </div>
                                    </header>

                                    <p className="dash-trend-detail" aria-live="polite">{collectionSelection ? `${collectionSelection.dateLabel}: ${formatCurrency(collectionSelection.amount)} · ${collectionSelection.loans} payment${collectionSelection.loans === 1 ? '' : 's'}` : collectionTrendAmount ? 'Select a chart point for daily details.' : 'No collections in this period.'}</p>
                                    <div className="dash-chart">
                                        <div className="dash-chart-axis">
                                            <span>{formatCurrency(collectionChartMax)}</span>
                                            <span>{formatCurrency(Math.round(collectionChartMax * 0.5))}</span>
                                            <span>₹0</span>
                                        </div>
                                        <div className="dash-chart-plot">
                                            <div className="dash-chart-canvas">
                                                <svg className="dash-chart-svg" viewBox="0 0 100 100" preserveAspectRatio="none" role="img" aria-label="Daily collection trend">
                                                    <defs>
                                                        <linearGradient id="dashCollectionFill" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="0%" stopColor="rgba(22, 163, 74, 0.28)" />
                                                            <stop offset="100%" stopColor="rgba(22, 163, 74, 0.02)" />
                                                        </linearGradient>
                                                    </defs>
                                                    <line className="dash-chart-grid" x1="0" y1="25" x2="100" y2="25" />
                                                    <line className="dash-chart-grid" x1="0" y1="50" x2="100" y2="50" />
                                                    <line className="dash-chart-grid" x1="0" y1="75" x2="100" y2="75" />
                                                    <polygon points={collectionChartArea} fill="url(#dashCollectionFill)" />
                                                    <polyline points={collectionChartLine} fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                                                </svg>
                                                <div className="dash-chart-points">
                                                    {collectionChartPoints.map((point) => (
                                                        <button type="button"
                                                            key={point.id}
                                                            className="dash-chart-point"
                                                            aria-label={`${point.tooltipLabel}: ${formatCurrency(point.amount)}`}
                                                            aria-pressed={selectedCollectionPoint === point.id}
                                                            onClick={() => setSelectedCollectionPoint(point.id)}
                                                            style={{ left: `${point.x}%`, top: `${point.y}%` }}
                                                            title={`${point.tooltipLabel}: ${formatCurrency(point.amount)}`}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="dash-chart-labels">
                                                {collectionChartPoints.filter((point, index) => index % Math.ceil(collectionChartPoints.length / 5) === 0 || index === collectionChartPoints.length - 1).map((point) => (
                                                    <span key={`${point.id}-lbl`}>{point.collectedAt}</span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </article>
                                <article className="dash-panel">
                                    <header className="dash-panel-head">
                                        <div>
                                            <p className="dash-panel-eyebrow">Recent loans</p>
                                            <h4>Latest disbursed &amp; active cases</h4>
                                        </div>
                                        <span className="dash-badge">{recentLoans.length} records</span>
                                    </header>
                                    <div className="dash-table-wrap">
                                        <table className="dash-table">
                                            <thead>
                                                <tr>
                                                    <th>Client</th>
                                                    <th>Type</th>
                                                    <th>Amount</th>
                                                    <th>Status</th>
                                                    <th>Disbursed</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {recentLoans.length > 0 ? recentLoans.slice(0, 6).map((loan) => (
                                                    <tr key={loan.id}>
                                                        <td>
                                                            <div className="dash-client">
                                                                <span className="dash-client-avatar">{String(loan.clientName || '?').charAt(0).toUpperCase()}</span>
                                                                <div className="dash-client-copy">
                                                                    <strong>{loan.clientName}</strong>
                                                                    <span>{loan.accountNumber}</span>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>{loan.loanType}</td>
                                                        <td className="dash-amount">{formatCurrency(loan.amount)}</td>
                                                        <td><span className="dash-status" style={{ color: statusColor(loan.status), background: `${statusColor(loan.status)}1a` }}>{loan.status}</span></td>
                                                        <td className="dash-muted">{loan.disbursedAt}</td>
                                                    </tr>
                                                )) : (
                                                    <tr>
                                                        <td colSpan="5" className="dash-empty">No recent loans found.</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </article>
                                <article className="dash-panel">
                                    <header className="dash-panel-head">
                                        <div>
                                            <p className="dash-panel-eyebrow">Collections</p>
                                            <h4>Collection This Month</h4>
                                        </div>
                                        <div className="dash-panel-metric">
                                            <strong>{stats.monthlyCollection?.percent || 0}%</strong>
                                            <span>collected</span>
                                        </div>
                                    </header>
                                    <div className="dash-collection-summary">
                                        <div className="dash-collection-row">
                                            <span>Due this month</span>
                                            <strong>{formatCurrency(stats.monthlyCollection?.dueAmount || 0)} <em>({stats.monthlyCollection?.dueCount || 0} loans)</em></strong>
                                        </div>
                                        <div className="dash-collection-row">
                                            <span>Collected so far</span>
                                            <strong>{formatCurrency(stats.monthlyCollection?.collectedAmount || 0)} <em>({stats.monthlyCollection?.collectedCount || 0} loans)</em></strong>
                                        </div>
                                        <div className="dash-bar-track dash-collection-track">
                                            <div className="dash-bar-fill" style={{ width: `${Math.min(stats.monthlyCollection?.percent || 0, 100)}%`, background: '#10b981' }} />
                                        </div>
                                        {!(stats.monthlyCollection?.dueCount) && (
                                            <p className="dash-empty">No loans due this month yet.</p>
                                        )}
                                    </div>
                                </article>
                            </div>
                            <aside className="dash-col-rail" aria-label="Targets and portfolio">
                                <article className="dash-panel">
                                    <header className="dash-panel-head">
                                        <div>
                                            <p className="dash-panel-eyebrow">Goal Tracking</p>
                                            <h4>This Month Target</h4>
                                        </div>
                                    </header>
                                    <div className="dash-target-summary" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', paddingBottom: '20px' }}>
                                        <div className="dash-donut-wrap" style={{ marginTop: '-20px' }}>
                                            <div className="dash-donut" style={{ background: `conic-gradient(#4f46e5 ${Math.min(targetProgress || 0, 100)}%, #e9eef6 0)` }}>
                                                <div className="dash-donut-hole">
                                                    <strong>{targetProgress == null ? '—' : `${targetProgress}%`}</strong>
                                                    <span>{targetAmount ? 'achieved' : 'No target set'}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ marginTop: '16px', textAlign: 'center' }}>
                                            <div style={{ fontSize: '1.06rem', fontWeight: '800', color: '#0f172a' }}>
                                                {stats.totalDisbursed || '₹0'}
                                            </div>
                                            <div style={{ fontSize: '0.73rem', color: '#64748b', marginTop: '4px' }}>
                                                of {formatCurrency(stats.monthlyTarget || 0)}
                                            </div>
                                        </div>
                                    </div>
                                </article>
                                <article className="dash-panel">
                                    <header className="dash-panel-head">
                                        <div>
                                            <p className="dash-panel-eyebrow">Portfolio mix</p>
                                            <h4>Loan distribution</h4>
                                        </div>
                                    </header>
                                    <div className="dash-donut-wrap">
                                        <div className="dash-donut" style={{ background: `conic-gradient(${portfolioConic})` }}>
                                            <div className="dash-donut-hole">
                                                <strong>{stats.totalLoans || 0}</strong>
                                                <span>loans</span>
                                            </div>
                                        </div>
                                        <ul className="dash-legend">
                                            {portfolioSegments.map((seg) => (
                                                <li key={seg.label}>
                                                    <span className="dash-legend-dot" style={{ background: seg.color }} />
                                                    <span className="dash-legend-label">{seg.label}</span>
                                                    <strong>{seg.percent}%</strong>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </article>
                            </aside>
                        </section>
                        <section className="dash-summary-grid" aria-label="Portfolio summary">
                                <article className="dash-panel">
                                    <header className="dash-panel-head">
                                        <div>
                                            <p className="dash-panel-eyebrow">At a glance</p>
                                            <h4>Key signals</h4>
                                        </div>
                                    </header>
                                    <div className="dash-mini-grid">
                                        <div className="dash-mini">
                                            <span>Due in 7 days</span>
                                            <strong>{dueSoonCount}</strong>
                                        </div>
                                        <div className="dash-mini">
                                            <span>Open tickets</span>
                                            <strong>{stats.openTickets || 0}</strong>
                                        </div>
                                        <div className="dash-mini">
                                            <span>Latest loan</span>
                                            <strong>{latestLoan ? formatCurrency(latestLoan.amount) : '₹0'}</strong>
                                        </div>
                                        <div className="dash-mini">
                                            <span>Latest payment</span>
                                            <strong>{latestPayment ? formatCurrency(latestPayment.amount) : '₹0'}</strong>
                                        </div>
                                    </div>
                                </article>
                                <article className="dash-panel">
                                    <header className="dash-panel-head">
                                        <div>
                                            <p className="dash-panel-eyebrow">Loan status</p>
                                            <h4>Recent breakdown</h4>
                                        </div>
                                    </header>
                                    <div className="dash-bars">
                                        {Object.entries(loanStatusBreakdown).length > 0 ? Object.entries(loanStatusBreakdown).map(([status, count]) => (
                                            <div key={status} className="dash-bar-row">
                                                <span className="dash-bar-label">{status}</span>
                                                <div className="dash-bar-track">
                                                    <div className="dash-bar-fill" style={{ width: `${Math.round((count / loanStatusMax) * 100)}%`, background: statusColor(status) }} />
                                                </div>
                                                <strong>{count}</strong>
                                            </div>
                                        )) : (
                                            <p className="dash-empty">No loan status data yet.</p>
                                        )}
                                    </div>
                                </article>
                                <article className="dash-panel">
                                    <header className="dash-panel-head">
                                        <div>
                                            <p className="dash-panel-eyebrow">Payment methods</p>
                                            <h4>Accepted channels</h4>
                                        </div>
                                        <span className="dash-badge">{recentPayments.length}</span>
                                    </header>
                                    <ul className="dash-list">
                                        {sortedPaymentMethods.length > 0 ? sortedPaymentMethods.map((entry) => (
                                            <li key={entry.method}>
                                                <span className="dash-list-label">{entry.method}</span>
                                                <strong>{entry.count}</strong>
                                            </li>
                                        )) : (
                                            <p className="dash-empty">No payment methods yet.</p>
                                        )}
                                    </ul>
                                </article>
                        </section>
                        </>}
                    </div>
                )}

                {activeTab === 'overview' && (
                    <div className="overview-content">
                        <div className="super-card">
                            <div className="super-card-header">
                                <h2>Detailed Business Overview</h2>
                                <div className="overview-actions">
                                    {hasPermission('reports.view') && <button className="secondary-btn" onClick={() => handleTabChange('reports')}>View Reports</button>}
                                    {hasPermission('analytics.view') && <button className="secondary-btn" onClick={() => handleTabChange('analytics')}>Open Analytics</button>}
                                </div>
                            </div>
                            <div className="insight-pulse-row">
                                <div className="pulse-card">
                                    <div className="pulse-label">Conversion</div>
                                    <div className="pulse-value">{conversionRate}%</div>
                                    <div className="pulse-sub">Active vs total loans</div>
                                    <div className="sparkbar">
                                        {[70, 82, 65, 90, conversionRate].map((v, i) => (
                                            <span key={i} style={{ height: `${Math.min(100, v)}%` }} />
                                        ))}
                                    </div>
                                </div>
                                <div className="pulse-card">
                                    <div className="pulse-label">Pending Rate</div>
                                    <div className="pulse-value warning">{pendingRate}%</div>
                                    <div className="pulse-sub">{stats.pendingLoans || 0} loans waiting</div>
                                    <div className="sparkbar warning">
                                        {[pendingRate, 22, 18, 15, 12].map((v, i) => (
                                            <span key={i} style={{ height: `${Math.min(100, v)}%` }} />
                                        ))}
                                    </div>
                                </div>
                                <div className="pulse-card">
                                    <div className="pulse-label">Avg Ticket</div>
                                    <div className="pulse-value">₹{avgTicketSize.toLocaleString('en-IN')}</div>
                                    <div className="pulse-sub">Per active loan</div>
                                    <div className="sparkbar info">
                                        {[avgTicketSize / 1000, 12, 14, 9, 11].map((v, i) => (
                                            <span key={i} style={{ height: `${Math.min(100, v)}%` }} />
                                        ))}
                                    </div>
                                </div>
                                <div className="pulse-card">
                                    <div className="pulse-label">Compliance</div>
                                    <div className="pulse-value success">{complianceScore}%</div>
                                    <div className="pulse-sub">Pending vs processed</div>
                                    <div className="sparkbar success">
                                        {[complianceScore, 88, 86, 90, 92].map((v, i) => (
                                            <span key={i} style={{ height: `${Math.min(100, v)}%` }} />
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="overview-insights">
                                {hasPermission('loans.view') && (
                                    <div className="insight-card success">
                                        <div className="insight-label">Loan Approval Rate</div>
                                        <div className="insight-value">{approvalRate}%</div>
                                        <div className="insight-sub">Across {totalLoanCount} reviewed loans</div>
                                    </div>
                                )}
                                {hasPermission('loans.view') && (
                                    <div className="insight-card warning">
                                        <div className="insight-label">Pending Loan Share</div>
                                        <div className="insight-value">{pendingRate}%</div>
                                        <div className="insight-sub">{stats.pendingLoans || 0} awaiting decision</div>
                                    </div>
                                )}
                                <div className="insight-card info">
                                    <div className="insight-label">Avg Disbursal / Client</div>
                                    <div className="insight-value">₹{avgDisbursalPerUser.toLocaleString('en-IN')}</div>
                                    <div className="insight-sub">{stats.totalDisbursed || '₹0'} total disbursed</div>
                                </div>
                                {hasPermission('tickets.manage') && (
                                    <div className="insight-card alert">
                                        <div className="insight-label">Support Backlog</div>
                                        <div className="insight-value">{openTicketBacklog}</div>
                                        <div className="insight-sub">Open tickets to triage</div>
                                    </div>
                                )}
                            </div>

                            {/* Role-focused mini dashboards */}
                            {adminSession?.role === 'sales' && (
                                <div className="role-focus-panel sales">
                                    <div className="role-panel-head">
                                        <div>
                                            <p className="eyebrow">Sales Pipeline</p>
                                            <h3>Stay on top of your leads</h3>
                                        </div>
                                        <div className="role-actions">
                                            <button className="secondary-btn" onClick={() => handleTabChange('leads')}>Go to Leads</button>
                                            <button className="primary-btn" onClick={() => handleTabChange('leads')}>Add Lead</button>
                                        </div>
                                    </div>
                                    <div className="role-kpi-grid">
                                        <div className="role-kpi">
                                            <span>Total Leads</span>
                                            <strong>{leadSummary.total}</strong>
                                        </div>
                                        <div className="role-kpi">
                                            <span>Assigned to Me</span>
                                            <strong>{leadSummary.myCount}</strong>
                                        </div>
                                        <div className="role-kpi">
                                            <span>Unassigned</span>
                                            <strong>{leadSummary.unassigned}</strong>
                                        </div>
                                        <div className="role-kpi">
                                            <span>Qualified</span>
                                            <strong>{leadSummary.byStatus.qualified || 0}</strong>
                                        </div>
                                    </div>
                                    <div className="role-pills">
                                        <div className="pill-stat">New: {leadSummary.byStatus.new || 0}</div>
                                        <div className="pill-stat warn">Contacted: {leadSummary.byStatus.contacted || 0}</div>
                                        <div className="pill-stat info">Converted: {leadSummary.byStatus.converted || 0}</div>
                                        <div className="pill-stat muted">Lost: {leadSummary.byStatus.lost || 0}</div>
                                    </div>
                                </div>
                            )}

                            {(adminSession?.role === 'branch' || adminSession?.role === 'accounts') && (
                                <div className="role-focus-panel branch">
                                    <div className="role-panel-head">
                                        <div>
                                            <p className="eyebrow">Disbursement Desk</p>
                                            <h3>Ready to disburse loans</h3>
                                        </div>
                                        <div className="role-actions">
                                            <button className="primary-btn" onClick={() => handleTabChange('loans')}>Open Loan Queue</button>
                                        </div>
                                    </div>
                                    <div className="role-kpi-grid">
                                        <div className="role-kpi">
                                            <span>Pending Approval</span>
                                            <strong>{stats.pendingLoans || 0}</strong>
                                        </div>
                                        <div className="role-kpi">
                                            <span>Active Loans</span>
                                            <strong>{stats.activeLoans || 0}</strong>
                                        </div>
                                        <div className="role-kpi">
                                            <span>Total Disbursed</span>
                                            <strong>{stats.totalDisbursed || '₹0'}</strong>
                                        </div>
                                        <div className="role-kpi">
                                            <span>System Health</span>
                                            <strong>{stats.systemHealth}</strong>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {adminSession?.role === 'collection' && (
                                <div className="role-focus-panel collection">
                                    <div className="role-panel-head">
                                        <div>
                                            <p className="eyebrow">Collections Control</p>
                                            <h3>Monitor repayments and follow-ups</h3>
                                        </div>
                                        <div className="role-actions">
                                            <button className="secondary-btn" onClick={() => handleTabChange('loans')}>View Active Loans</button>
                                        </div>
                                    </div>
                                    <div className="role-kpi-grid">
                                        <div className="role-kpi">
                                            <span>Active Loans</span>
                                            <strong>{stats.activeLoans || 0}</strong>
                                        </div>
                                        <div className="role-kpi">
                                            <span>Pending Loans</span>
                                            <strong>{stats.pendingLoans || 0}</strong>
                                        </div>
                                        <div className="role-kpi">
                                            <span>Total Loans</span>
                                            <strong>{stats.totalLoans || 0}</strong>
                                        </div>
                                        <div className="role-kpi">
                                            <span>Support Tickets</span>
                                            <strong>{stats.openTickets || 0}</strong>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="overview-grid enhanced-overview-grid">
                                {hasPermission('users.view_all') && (
                                    <div className="overview-item card-clickable" onClick={() => handleTabChange('users')}>
                                        <div className="overview-icon blue">👥</div>
                                        <div>
                                            <h4>{stats.totalUsers?.toLocaleString?.() || stats.totalUsers}</h4>
                                            <p>Total Clients</p>
                                        </div>
                                    </div>
                                )}

                                {hasPermission('admins.view_all') && (
                                    <div className="overview-item card-clickable" onClick={() => handleTabChange('admins')}>
                                        <div className="overview-icon purple">🔐</div>
                                        <div>
                                            <h4>{stats.totalAdmins || 0}</h4>
                                            <p>Administrator Accounts</p>
                                        </div>
                                    </div>
                                )}

                                {hasPermission('analytics.view') && (
                                    <div className="overview-item card-clickable" onClick={() => handleTabChange('analytics')}>
                                        <div className="overview-icon green">💳</div>
                                        <div>
                                            <h4>{stats.totalDisbursed || '₹0'}</h4>
                                            <p>Total Disbursed</p>
                                        </div>
                                    </div>
                                )}

                                {hasPermission('loans.view') && (
                                    <div className="overview-item card-clickable" onClick={() => handleTabChange('loans')}>
                                        <div className="overview-icon purple">📈</div>
                                        <div>
                                            <h4>{stats.totalLoans || 0}</h4>
                                            <p>Total Loans</p>
                                        </div>
                                    </div>
                                )}

                                {hasPermission('activity.view') && (
                                    <div className="overview-item card-clickable" onClick={() => handleTabChange('activity')}>
                                        <div className="overview-icon orange">⏳</div>
                                        <div>
                                            <h4>{stats.pendingLoans || stats.pendingApprovals || 0}</h4>
                                            <p>Pending Loans</p>
                                        </div>
                                    </div>
                                )}

                                {hasPermission('loans.view') && (
                                    <div className="overview-item card-clickable" onClick={() => handleTabChange('loans')}>
                                        <div className="overview-icon blue">📡</div>
                                        <div>
                                            <h4>{stats.activeLoans || 0}</h4>
                                            <p>Active Loans</p>
                                        </div>
                                    </div>
                                )}

                                {hasPermission('leads.view') && (
                                    <div className="overview-item card-clickable" onClick={() => handleTabChange('leads')}>
                                        <div className="overview-icon pink">🎯</div>
                                        <div>
                                            <h4>Leads</h4>
                                            <p>New Opportunities</p>
                                        </div>
                                    </div>
                                )}

                                {hasPermission('tickets.manage') && (
                                    <div className="overview-item card-clickable" onClick={() => handleTabChange('support')}>
                                        <div className="overview-icon purple">📜</div>
                                        <div>
                                            <h4>{stats.openTickets || 0}</h4>
                                            <p>Open Support Tickets</p>
                                        </div>
                                    </div>
                                )}

                                <div className="overview-item">
                                    <div className="overview-icon green">🩺</div>
                                    <div>
                                        <h4>{stats.systemHealth || 'Excellent'}</h4>
                                        <p>System Health</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'admins' && hasPermission('admins.view_all') && (
                    <>
                        <div className="super-card">
                            <div className="super-card-header user-header">
                                <div className="header-left">
                                    <h2>Administrator Accounts</h2>
                                    <p className="header-sub">Manage staff accounts, roles, access and credentials.</p>
                                </div>
                                <div className="header-actions">
                                    <button className="action-header-btn create-admin" onClick={() => setShowCreateAdminModal(true)}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                            <circle cx="8.5" cy="7" r="4"></circle>
                                            <line x1="20" y1="8" x2="20" y2="14"></line>
                                            <line x1="23" y1="11" x2="17" y2="11"></line>
                                        </svg>
                                        Create Admin
                                    </button>
                                    <button className="action-header-btn settings" onClick={() => handleTabChange('settings')}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="3"></circle>
                                            <path d="M12 1v6m0 6v6M7.05 7.05l4.95 4.95m0 0l4.95 4.95M7.05 16.95l4.95-4.95m0 0l4.95-4.95"></path>
                                        </svg>
                                        Settings
                                    </button>
                                </div>
                            </div>

                            <div className="admin-summary-tiles">
                                <div className="admin-summary-tile"><span className="ast-val">{adminSummary.total}</span><span className="ast-lbl">Total Admins</span></div>
                                <div className="admin-summary-tile"><span className="ast-val ast-ok">{adminSummary.active}</span><span className="ast-lbl">Active</span></div>
                                <div className="admin-summary-tile"><span className="ast-val ast-off">{adminSummary.inactive}</span><span className="ast-lbl">Inactive</span></div>
                                <div className="admin-summary-tile"><span className="ast-val">{adminSummary.roles}</span><span className="ast-lbl">Distinct Roles</span></div>
                            </div>

                            <div className="admin-role-breakdown">
                                {adminRoleBreakdown.map(r => (
                                    <span key={r.role} className="admin-role-chip">
                                        <span className={`role-dot ${r.cls}`} />
                                        {r.label}
                                        <strong>{r.count}</strong>
                                    </span>
                                ))}
                            </div>

                            <div className="admin-toolbar">
                                <input
                                    type="text"
                                    className="admin-toolbar-search"
                                    placeholder="Search name, email or phone…"
                                    value={adminSearch}
                                    onChange={(e) => setAdminSearch(e.target.value)}
                                />
                                <select className="admin-toolbar-select" value={adminRoleFilter} onChange={(e) => setAdminRoleFilter(e.target.value)}>
                                    <option value="all">All roles</option>
                                    {adminRoleOptions.map(r => <option key={r} value={r}>{prettyRoleName(r)}</option>)}
                                </select>
                                <select className="admin-toolbar-select" value={adminStatusFilter} onChange={(e) => setAdminStatusFilter(e.target.value)}>
                                    <option value="all">All status</option>
                                    <option value="Active">Active</option>
                                    <option value="Inactive">Inactive</option>
                                </select>
                                <span className="admin-toolbar-count">{filteredAdmins.length} of {admins.length}</span>
                            </div>

                            <div className="super-table-container">
                                <table className="super-table admin-table">
                                    <thead>
                                        <tr>
                                            <th>Administrator</th>
                                            <th>Role</th>
                                            <th>Contact</th>
                                            <th>Access</th>
                                            <th>Status</th>
                                            <th>Last Login</th>
                                            <th>Created</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredAdmins.length === 0 ? (
                                            <tr><td colSpan={8} className="admin-empty-row">No administrators match your filters.</td></tr>
                                        ) : filteredAdmins.map(admin => (
                                            <tr key={admin.id}>
                                                <td>
                                                    <div className="admin-identity">
                                                        <span className="admin-avatar" data-role={admin.role}>{adminInitials(admin.name)}</span>
                                                        <div className="admin-identity-text">
                                                            <span className="admin-identity-name">{admin.name}</span>
                                                            <span className="admin-identity-email">{admin.email}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>{getRoleBadge(admin.role)}</td>
                                                <td className="admin-phone">{admin.phone || '—'}</td>
                                                <td>
                                                    <span className="admin-perm-pill" title={`${admin.permissionCount} permissions granted`}>
                                                        {admin.permissionCount} perms
                                                    </span>
                                                </td>
                                                <td>
                                                    <button
                                                        className={`status-badge ${admin.status === 'Active' ? 'status-success' : 'status-danger'}`}
                                                        style={{ border: 'none', cursor: 'pointer', fontWeight: 'bold' }}
                                                        onClick={() => toggleAdminStatus(admin.id, admin.status)}
                                                        title="Click to toggle status"
                                                    >
                                                        {admin.status}
                                                    </button>
                                                </td>
                                                <td className="admin-date" title={admin.lastLogin}>{relLastLogin(admin.lastLoginAt)}</td>
                                                <td className="admin-date">{admin.createdAt ? new Date(admin.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }) : '—'}</td>
                                                <td>
                                                    <div className="admin-row-actions">
                                                        <button className="admin-action-btn edit" title="Edit admin" onClick={() => handleEditAdmin(admin)}>Edit</button>
                                                        {hasPermission('users.reset_password') && (
                                                            <button className="admin-action-btn reset" title="Reset password" onClick={() => handleResetAdminPassword(admin)}>Reset PW</button>
                                                        )}
                                                        {adminSession?.role === 'super_admin' && (
                                                            <button className="admin-action-btn delete" title="Delete admin" onClick={() => handleDeleteAdmin(admin.id)}>Delete</button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}

                {activeTab === 'users' && hasPermission('users.view_all') && (
                    <div className="super-card">
                        <div className="super-card-header user-header">
                            <div className="header-left">
                                <h2>User Management</h2>
                                <p className="header-sub">Search, create, and manage client users in one place.</p>
                            </div>
                            <div className="header-actions search-row">
                                <input
                                    type="text"
                                    className="search-input"
                                    placeholder="Search by name, email, phone..."
                                    value={userSearch}
                                    onChange={(e) => setUserSearch(e.target.value)}
                                />
                                <button className="secondary-btn" onClick={fetchAllData}>Search</button>
                                <button className="action-header-btn create-user" onClick={() => setShowCreateUserModal(true)}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                        <circle cx="8.5" cy="7" r="4"></circle>
                                        <line x1="20" y1="8" x2="20" y2="14"></line>
                                        <line x1="23" y1="11" x2="17" y2="11"></line>
                                    </svg>
                                    Create User
                                </button>
                            </div>
                        </div>
                        <div className="super-table-container">
                            <table className="super-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Phone</th>
                                        <th>Status</th>
                                        <th>KYC Status</th>
                                        <th>Joined</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentUsers.map(user => (
                                        <tr key={user.id}>
                                            <td className="admin-name">{user.name}</td>
                                            <td className="admin-email">{user.email}</td>
                                            <td className="admin-phone">{user.phone || '-'}</td>
                                            <td>
                                                <button
                                                    className={`status-badge ${user.status === 'Active' ? 'status-success' :
                                                        user.status === 'Restricted' ? 'status-warning' :
                                                            'status-danger'
                                                        }`}
                                                    style={{ border: 'none', cursor: 'pointer', fontWeight: 'bold' }}
                                                    onClick={() => {
                                                        const newStatus = user.status === 'Active' ? 'Inactive' : 'Active';
                                                        changeUserStatus(user.id, newStatus);
                                                    }}
                                                    title="Click to toggle status"
                                                >
                                                    {user.status}
                                                </button>
                                            </td>
                                            <td>
                                                <span className={`status-badge ${user.kyc === 'completed' ? 'status-success' :
                                                    user.kyc === 'in_progress' ? 'status-warning' :
                                                        user.kyc === 'rejected' ? 'status-danger' :
                                                            'status-pending'
                                                    }`}>
                                                    {user.kyc === 'completed' ? 'Verified' :
                                                        user.kyc === 'in_progress' ? 'In Progress' :
                                                            user.kyc === 'rejected' ? 'Rejected' :
                                                                'Pending'}
                                                </span>
                                            </td>
                                            <td className="admin-date">{user.joinDate}</td>
                                            <td>
                                                <div className="admin-row-actions">
                                                    <button className="admin-action-btn view" title="View Details" onClick={() => handleViewUser(user)}>
                                                        View
                                                    </button>
                                                    <button className="admin-action-btn edit" title="Edit User" onClick={() => handleEditUser(user)}>
                                                        Edit
                                                    </button>
                                                    {adminSession?.role === 'super_admin' && (
                                                        <button className="admin-action-btn delete" title="Delete User" onClick={() => handleDeleteUser(user.id)}>
                                                            Delete
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {usersTotalPages > 1 && (
                            <div className="pagination-controls" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1rem' }}>
                                <button 
                                    className="secondary-btn" 
                                    disabled={usersPage === 1}
                                    onClick={() => setUsersPage(prev => Math.max(1, prev - 1))}
                                >
                                    Previous
                                </button>
                                <span>Page {usersPage} of {usersTotalPages}</span>
                                <button 
                                    className="secondary-btn"
                                    disabled={usersPage === usersTotalPages}
                                    onClick={() => setUsersPage(prev => Math.min(usersTotalPages, prev + 1))}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'logs' && hasPermission('logs.view') && (
                    <div className="super-card">
                        <div className="super-card-header">
                            <h2>System Activity Logs</h2>
                            <button className="export-btn">
                                📤 Export Logs
                            </button>
                        </div>
                        <div className="logs-container">
                            {systemLogs.map(log => (
                                <div key={log.id} className={`log-item ${getSeverityClass(log.severity)}`}>
                                    <div className="log-icon">
                                        {log.severity === 'success' && '✅'}
                                        {log.severity === 'warning' && '⚠️'}
                                        {log.severity === 'error' && '❌'}
                                        {log.severity === 'info' && 'ℹ️'}
                                    </div>
                                    <div className="log-content">
                                        <div className="log-header">
                                            <h4>{log.action}</h4>
                                            <span className="log-time">{log.time}</span>
                                        </div>
                                        <p className="log-details">{log.details}</p>
                                        <span className="log-user">By: {log.user}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Access Denied Guard for dynamic tabs */}
                {activeTab !== 'overview' && tabPermissions[activeTab] && !hasPermission(tabPermissions[activeTab]) && (
                    <div className="super-card access-denied">
                        <div style={{ textAlign: 'center', padding: '50px' }}>
                            <div style={{ fontSize: '44px', marginBottom: '20px' }}>🚫</div>
                            <h2>Access Denied</h2>
                            <p>You do not have the required permissions to view this section.</p>
                            <button className="primary-btn" style={{ marginTop: '20px' }} onClick={() => handleTabChange('overview')}>
                                Return to Overview
                            </button>
                        </div>
                    </div>
                )}

                {/* New Tabs — each is a separate chunk (see tabRegistry.js), and each
                    stays mounted (just hidden) once you've opened it once this
                    session, via renderKeepAlive above. First visit to a tab still
                    shows the loading spinner briefly; every visit after that is
                    instant, with no refetch. */}
                {renderKeepAlive('globalSearch', hasPermission('search.view'), <GlobalSearchTab adminSession={adminSession} />)}
                {renderKeepAlive('investorDashboard', hasPermission('investor.dashboard'), <InvestorDashboardTab adminSession={adminSession} />)}
                {renderKeepAlive('collectionReport', hasPermission('investor.dashboard'), <CollectionReportTab adminSession={adminSession} />)}
                {renderKeepAlive('analytics', hasPermission('analytics.view'), <AnalyticsTab adminSession={adminSession} />)}
                {renderKeepAlive('permissions', hasPermission('permissions.manage'), <PermissionsTab adminSession={adminSession} />)}
                {renderKeepAlive('roles', hasPermission('roles.manage'), <RolesTab adminSession={adminSession} />)}
                {renderKeepAlive('settings', hasPermission('settings.manage'), <SettingsTab adminSession={adminSession} />)}
                {renderKeepAlive('database', hasPermission('database.manage'), <DatabaseTab adminSession={adminSession} />)}
                {renderKeepAlive('sanctionLetters', hasPermission('database.manage'), <DataRecordTab adminSession={adminSession} recordType="sanctionLetters" />)}
                {renderKeepAlive('crifData', hasPermission('database.manage'), <DataRecordTab adminSession={adminSession} recordType="crifData" />)}
                {renderKeepAlive('crifJson', hasPermission('database.manage'), <DataRecordTab adminSession={adminSession} recordType="crifJson" />)}
                {renderKeepAlive('crifHtml', hasPermission('database.manage'), <DataRecordTab adminSession={adminSession} recordType="crifHtml" />)}
                {renderKeepAlive('aadhaarInfo', hasPermission('database.manage'), <DataRecordTab adminSession={adminSession} recordType="aadhaarInfo" />)}
                {renderKeepAlive('bankStatements', hasPermission('database.manage'), <DataRecordTab adminSession={adminSession} recordType="bankStatements" />)}
                {renderKeepAlive('breDecision', hasPermission('database.manage'), <BreDecisionTab adminSession={adminSession} />)}
                {renderKeepAlive('activity', hasPermission('activity.view'), <ActivityMonitorTab adminSession={adminSession} />)}
                {renderKeepAlive('reports', hasPermission('reports.view'), <ReportsTab adminSession={adminSession} />)}
                {renderKeepAlive('support', hasPermission('tickets.manage'), <SupportTab adminSession={adminSession} />)}
                {renderKeepAlive('api', hasPermission('api.manage'), <ApiManagementTab adminSession={adminSession} />)}
                {renderKeepAlive('whatsappMarketing', hasPermission(tabPermissions['whatsappMarketing']), <WhatsAppMarketingTab adminSession={adminSession} />)}
                {renderKeepAlive('payments', hasPermission(tabPermissions['payments']), <PaymentGatewayTab adminSession={adminSession} />)}
                {renderKeepAlive('kyc', hasPermission(tabPermissions['kyc']), <KycManagementTab adminSession={adminSession} />)}
                {renderKeepAlive('loans', hasPermission(tabPermissions['loans']), <LoansTab adminSession={adminSession} initialActiveView="loans" />)}
                {renderKeepAlive('queue-credit-exec', hasPermission(tabPermissions['loans']), <LoansTab adminSession={adminSession} queueFilter="PENDING_CREDIT_EXEC" />)}
                {renderKeepAlive('queue-credit-head', ['credit_head', 'admin', 'super_admin'].includes(adminSession?.role) || hasPermission('queue.credit_head'), <LoansTab adminSession={adminSession} queueFilter="PENDING_CREDIT_HEAD" />)}
                {renderKeepAlive('queue-deep-review', adminSession?.role === 'super_admin' || adminSession?.role === 'admin' || hasPermission('queue.deep_review'), <LoansTab adminSession={adminSession} queueFilter="PENDING_DEEP_REVIEW" />)}
                {renderKeepAlive('queue-esign', ['credit_head', 'admin', 'super_admin'].includes(adminSession?.role) || hasPermission('queue.disbursal'), <LoansTab adminSession={adminSession} queueFilter="ESIGN_PENDING" />)}
                {renderKeepAlive('queue-disbursal', ['credit_head', 'admin', 'super_admin'].includes(adminSession?.role) || hasPermission('queue.disbursal'), <LoansTab adminSession={adminSession} queueFilter="APPROVAL_NEEDED_FOR_DISBURSAL" />)}
                {renderKeepAlive('bankAndTransactions', ['credit_head', 'admin', 'super_admin'].includes(adminSession?.role) || hasPermission('queue.disbursal'), <BankAndTransactionsTab adminSession={adminSession} />)}
                {renderKeepAlive('queue-collection', ['collection_head', 'collection_executive', 'admin', 'super_admin'].includes(adminSession?.role) || hasPermission('queue.collection'), <LoansTab adminSession={adminSession} queueFilter="Active" />)}
                {renderKeepAlive('queue-reloan', adminSession?.role === 'super_admin' || adminSession?.role === 'admin' || hasPermission('queue.reloan'), <LoansTab adminSession={adminSession} queueFilter="RELOAN" />)}
                {renderKeepAlive('queue-closed', ['collection_head', 'collection_executive', 'credit_executive', 'admin', 'super_admin'].includes(adminSession?.role) || hasPermission('queue.collection') || hasPermission('queue.closed'), <LoansTab adminSession={adminSession} queueFilter="Closed" />)}
                {renderKeepAlive('leads', hasPermission(tabPermissions['leads']), <LeadsTab adminSession={adminSession} />)}
                {renderKeepAlive('salesLeads', hasPermission(tabPermissions['salesLeads']), <SalesLeadsTab adminSession={adminSession} />)}
                {renderKeepAlive('myDashboard', ['credit_executive', 'credit_head', 'sales_executive', 'sales_head', 'collection_executive', 'collection_head'].includes(adminSession?.role), <MyDashboardTab adminSession={adminSession} refreshSignal={dashboardRefreshSignal} />)}
                {renderKeepAlive('staffTargets', adminSession?.role === 'super_admin', <StaffTargetsTab adminSession={adminSession} />)}
            </div>

            {/* Modals — always mounted (isOpen toggles their own visibility), so
                these can't defer their chunk fetch until first opened the way the
                tabs above do; fallback={null} just means no visible flash while
                that fetch is in flight, since a closed modal renders nothing. */}
            <Suspense fallback={null}>
            {showCreateAdminModal && <CreateAdminModal
                isOpen={showCreateAdminModal}
                onClose={() => setShowCreateAdminModal(false)}
                onSuccess={handleAdminCreated}
                createdBy={adminSession?.userId}
            />}

            {showCreateUserModal && <CreateUserModal
                isOpen={showCreateUserModal}
                onClose={() => setShowCreateUserModal(false)}
                onSuccess={handleUserCreated}
                createdBy={adminSession?.userId}
            />}

            {showEditAdminModal && <EditAdminModal
                isOpen={showEditAdminModal}
                onClose={() => setShowEditAdminModal(false)}
                onSuccess={fetchAllData}
                admin={editingAdmin}
                adminData={adminSession}
                onImpersonate={handleImpersonate}
            />}

            {showEditUserModal && <EditUserModal
                isOpen={showEditUserModal}
                onClose={() => setShowEditUserModal(false)}
                onSuccess={fetchAllData}
                user={editingUser}
                adminData={adminSession}
                onImpersonate={handleImpersonate}
            />}

            </Suspense>

            {/* Custom Modal for Alerts/Confirmations */}
            {modalConfig.show && (
                <div className="system-modal-overlay">
                    <div className="system-modal-content bento-look">
                        <div className="system-modal-header">
                            <h3>{modalConfig.title}</h3>
                            <button className="close-x" onClick={closeModal}>&times;</button>
                        </div>
                        <div className="system-modal-body">
                            <p>{modalConfig.message}</p>
                        </div>
                        <div className="system-modal-footer">
                            {modalConfig.type === 'confirm' && (
                                <button className="modal-btn-cancel" onClick={closeModal}>
                                    {modalConfig.cancelText || 'Cancel'}
                                </button>
                            )}
                            <button className="modal-btn-confirm" onClick={() => {
                                if (modalConfig.onConfirm) modalConfig.onConfirm();
                                closeModal();
                            }}>
                                {modalConfig.confirmText || 'OK'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SuperAdminDashboardPage;
